import {
  TemplateDefinition,
  TemplateText,
  TemplateCommonSection,
  TemplatePageSection,
  TemplateLine,
  TemplateImage,
  TemplateRectangle,
  TemplateSummary,
  TemplateDataPlate,
  TableColumn,
  ElementLocationInSection,
  Validation,
  Options,
  SubTotal,
  TemplateSignature,
  TemplateDropDownList,
  TemplateIso,
  TemplateMeasurementPoint,
  TemplateSection,
  TemplateTable,
  TemplateVisualEvents,
  TemplateTableOffline,
  TemplateDataPlateImageInfo,
  TemplateDataPlateText
} from '../_offline/models/report-preview/report-template';
import {
  ReportPreviewMetadata,
  PropertyPreviewMetadata,
  FieldType,
  UpdateMetadata,
  TypeOfJSON
} from '../_services/offline-report-preview.service';
import {
  Observable,
  Subject,
  Subscription
} from 'rxjs';
import { DYNAMIC_SELECT_ITEM } from '../app.constants';
import { ReportSection } from '../_offline/models/report-preview/report-section';
import { ReportTable } from '../_offline/models/report-preview/report-table';
import { ReportISO } from '../_offline/models/report-preview/report-iso';
import { ReportCustomElement } from '../_offline/models/report-preview/report-custom-element';
import { ReportDropDownList } from '../_offline/models/report-preview/report-dropdown-list';
import { ReportMeasurementPoint } from '../_offline/models/report-preview/report-measurement-point';
import { ReportVisualEvents } from '../_offline/models/report-preview/report-visual-events';

/** *****************************************************************/
export type ElementType = 'Page' | 'Text' | 'Line' | 'Image' | 'Rectangle' | 'Summary' | 'Picture'
| 'DataPlate' | 'MeasurementPoint' | 'VisualEvents' | 'Signature' | 'Section' | 'Table' | 'InteractiveIso' | 'DropDownList' | 'CustomElement';
export namespace ElementType {
  export const Page: ElementType = 'Page';
  export const Text: ElementType = 'Text';
  export const Line: ElementType = 'Line';
  export const Image: ElementType = 'Image';
  export const Rectangle: ElementType = 'Rectangle';
  export const Summary: ElementType = 'Summary';
  export const Picture: ElementType = 'Picture';
  export const DataPlate: ElementType = 'DataPlate';
  export const MeasurementPoint: ElementType = 'MeasurementPoint';
  export const VisualEvents: ElementType = 'VisualEvents';
  export const Signature: ElementType = 'Signature';
  export const Section: ElementType = 'Section';
  export const Table: ElementType = 'Table';
  export const InteractiveIso: ElementType = 'InteractiveIso';
  export const DropDownList: ElementType = 'DropDownList';
  export const CustomElement: ElementType = 'CustomElement';

  export function cast(value: string): ElementType {
    let rtnType: ElementType = 'Text'; // 仮のデフォルト値
    const types: ElementType[] = [
      ElementType.Page,
      ElementType.Text,
      ElementType.Line,
      ElementType.Image,
      ElementType.Rectangle,
      ElementType.Summary,
      ElementType.Picture,
      ElementType.DataPlate,
      ElementType.MeasurementPoint,
      ElementType.VisualEvents,
      ElementType.Signature,
      ElementType.Section,
      ElementType.Table,
      ElementType.InteractiveIso,
      ElementType.DropDownList,
      ElementType.CustomElement
    ];
    for (const type of types) {
      if (type.toLowerCase() === value.trim().toLowerCase()) {
        rtnType = type;
        break;
      }
    }
    return rtnType;
  }
}

export namespace Location {
  export const Header: Location = 'Header';
  export const Body: Location = 'Body';
  export const Footer: Location = 'Footer';
  export const Page: Location = 'Page';

  export function cast(value: string): Location {
    let rtnLoc: Location = 'Body'; // 仮のデフォルト値
    const locs: Location[] = [
      Location.Header,
      Location.Body,
      Location.Footer,
      Location.Page
    ];
    for (const loc of locs) {
      if (loc.toLowerCase() === value.trim().toLowerCase()) {
        rtnLoc = loc;
        break;
      }
    }
    return rtnLoc;
  }
}
export type Location = 'Header' | 'Body' | 'Footer' | 'Page';

export interface FromToDetail {
  location: Location;
  pageId?: string;
}
export interface FromTo {
  from: FromToDetail;
  to: FromToDetail;
}

/** *****************************************************************/
type PropertyType = 'Report Font' | 'Size Unit' | 'Page Margin' | 'Paper Size' | 'Page Header' | 'Page Footer';
type PropOrElemType = PropertyType | ElementType;
type TemplateJSON = TemplateDefinition
| TemplateCommonSection
| TemplatePageSection
| TemplateText
| TemplateLine
| TemplateImage
| TemplateRectangle
| TemplateSummary
| TemplateDataPlate
| TemplateTable
| TemplateTableOffline
| TemplateVisualEvents
| TemplateMeasurementPoint
| TemplateSignature
| TemplateSection
| TemplateTable
| TemplateIso
| TemplateDropDownList;

/** *****************************************************************/

export const TABLE_RULES = [
  { label: 'none', value: 'none' },
  { label: 'groups', value: 'groups' },
  { label: 'rows', value: 'rows' },
  { label: 'cols', value: 'cols' },
  { label: 'all', value: 'all' }
];

export const BORDER_STYLE = [
  { label: 'none', value: 'none' },
  { label: 'hidden', value: 'hidden' },
  { label: 'solid', value: 'solid' },
  { label: 'double', value: 'double' },
  { label: 'groove', value: 'groove' },
  { label: 'ridge', value: 'ridge' },
  { label: 'inset', value: 'inset' },
  { label: 'outset', value: 'outset' },
  { label: 'dashed', value: 'dashed' },
  { label: 'dotted', value: 'dotted' }
];

export const FONT_FAMILY = [
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'sans-serif', value: 'sans-serif' },
  { label: 'serif', value: 'serif' },
  { label: 'Arial', value: 'Arial' },
  { label: 'Calibri', value: 'Calibri' }
];

/** *****************************************************************/
export abstract class ReportProperties {

  protected readonly type: PropOrElemType;

  private uuid: string;
  private isExist$: boolean; // true: 既存, false: 新規(＝テンプレートJSONに記載がある状態がtrue)
  private isSaved$: boolean; // true: 保存済み, false: 未保存(＝サーバー側に保存されているテンプレートJSONと一致している状態がtrue)

  // px値の算出に必要になるdpiの値は、下記W3Cの勧告仕様に準拠して「96dpi」とする
  // https://www.w3.org/TR/css3-values/#absolute-lengths
  private readonly dpi = 96;
  private readonly mmPerInch = 25.4; // 25.4mm = 1inch

  // 連鎖的なプロパティ更新を行う関数群
  private chainUpdateSubscribers: Array<Subscription>;

  // 画面上のプロパティ一覧と紐付くメタ情報
  private previewMetadatas: PropertyPreviewMetadata[];
  // サーバー側に保持している値と一致するメタ情報
  private originMetadatas: PropertyPreviewMetadata[];

  /**
   * コンストラクタ
   */
  constructor(type: PropOrElemType, id?: string) {
    if (id) {
      this.uuid = id;
    } else {
      this.uuid = this.generateUUID();
    }
    this.type = type;
  }

  /**
   * 自インスタンスのID（UUID）を返却する
   */
  public getId(): string {
    return this.uuid;
  }

  /**
   * 自インスタンスのID（UUID）を返却する
   */
  public generateId(): string {
    this.uuid = this.generateUUID();
    return this.uuid;
  }

  /**
   * プロパティ一覧に表示する自インスタンスのタイトルを返却する
   */
  public getTitle(): string {
    return this.type;
  }

  /**
   * プロパティ一覧を返却する
   */
  public getProperties(): PropertyPreviewMetadata[] {
    return this.previewMetadatas;
  }

  public getMapProperties(): Map<string, PropertyPreviewMetadata[]> {
    const map = new Map<string, PropertyPreviewMetadata[]>();
    this.previewMetadatas.forEach(metadata => {
      if (map.has(metadata.group)) {
        map.get(metadata.group).push(metadata);
      } else {
        const metadataArray: PropertyPreviewMetadata[] = [metadata];
        map.set(metadata.group, metadataArray);
      }
    });
    return map;
  }

  /**
   * 自インスタンスで保持しているテンプレートJSONが保存済みか
   */
  public isSaved(): boolean {
    return this.isSaved$;
  }

  /**
   * 自インスタンスで保持しているテンプレートJSONが既存データか
   */
  public isExisting(): boolean {
    return this.isExist$;
  }

  /**
   * 他インスタンスの更新が発生したタイミングで自インスタンスの更新を行う処理を登録する
   */
  public setChainUpdate() {
    if (!this.chainUpdateSubscribers) {
      this.chainUpdateSubscribers = new Array<Subscription>();
    } else if (this.chainUpdateSubscribers && this.chainUpdateSubscribers.length > 0) {
      this.releaseChainUpdate();
    }
    // 継承クラス側で実装された、連鎖的なプロパティ更新処理の登録を行う
    this.setChainUpdateFunction(this.chainUpdateSubscribers);
  }

  /**
   * 他インスタンスの更新が発生したタイミングで自インスタンスの更新を行う処理を解除する
   */
  public releaseChainUpdate() {
    for (const subscribe of this.chainUpdateSubscribers) {
      subscribe.unsubscribe();
    }
    this.chainUpdateSubscribers = new Array<Subscription>();
  }

  /**
   * 更新データを自インスタンスに設定する
   * @param metadata 更新データ
   */
  public updateProperties(metadata: UpdateMetadata) {

    // 継承クラス側で定義されたプロパティ更新処理を行う
    this.setUpdateMetadata(metadata);

    // // 未保存状態にする
    // this.isSaved$ = false;
  }

  // --- 以降は基底クラス・継承クラス内でのみ利用可能な関数 ---

  // 継承クラス側に実装を強要する関数
  protected abstract getTemplateJSON(): TemplateJSON;
  protected abstract getPreviewMetadata(): PropertyPreviewMetadata[];
  protected abstract setChainUpdateFunction(subscribers: Array<Subscription>);
  protected abstract setUpdateMetadata(metadata: UpdateMetadata, );

  protected getType(): PropOrElemType {
    return this.type;
  }

  protected setPreviewMetadata() {
    if (!this.previewMetadatas && !this.originMetadatas) {
      this.previewMetadatas = this.getPreviewMetadata();
      this.originMetadatas = this.getPreviewMetadata();
      this.updateMetadataByJSON(this.originMetadatas, this.getTemplateJSON());
      this.updateMetadataByJSON(this.previewMetadatas, this.getTemplateJSON());
    }
  }

  protected setPreviewValue(name: string, value: TypeOfJSON) {
    let preview: PropertyPreviewMetadata;
    let origin: PropertyPreviewMetadata;
    for (const metadata of this.previewMetadatas) {
      if (metadata.name === name) {
        preview = metadata;
        break;
      }
    }
    for (const metadata of this.originMetadatas) {
      if (metadata.name === name) {
        origin = metadata;
        break;
      }
    }
    if (preview) {
      preview.value = value;
      preview.isEdited = (origin.value !== preview.value);
    }

    // プロパティ全体の保存済み状況を更新する
    this.isSaved$ = true;
    for (const metadata of this.previewMetadatas) {
      if (metadata.isEdited) {
        this.isSaved$ = false;
        break;
      }
    }
  }

  /**
   * 自インスタンスの保存状態を更新する
   * @param value isSavedに設定する値
   */
  protected setSaveState(value: boolean): void {
    this.isSaved$ = value;
  }

  /**
   * 自インスタンスの既存データ状態を更新する
   * @param value isExistに設定する値
   */
  protected setExistState(value: boolean): void {
    this.isExist$ = value;
  }

  /**
   * px値を指定された単位の値に変換する
   * @param px 変換前の値（px値）
   * @param unit 変換後の単位
   */
  protected fromPixel(px: number, unit: string): number {
    let rtn = px;
    if (px != null) {
      switch (unit) {
        case 'mm':
          // rtn(mm値) = inch ÷ dpi × px値
          rtn = this.mmPerInch / this.dpi * px;
          break;
        case 'cm':
          // rtn(cm値) = (inch ÷ 10) ÷ dpi × px値
          rtn = (this.mmPerInch / 10) / this.dpi * px;
          break;
        case 'in':
          // rtn(in値) = px値 ÷ dpi
          rtn = px / this.dpi;
          break;
        case 'pt':
          // rtn(pt値) = (px値 ÷ dpi) × 72
          rtn = (px / this.dpi) * 72;
          break;
        default:
          // 他にもCSS上で指定可能な単位はいくつかあるが、ph2のタイミングでは上記のみ実装を入れておく
          break;
      }
      rtn = this.round(rtn);
    }
    return rtn;
  }

  /**
   * 指定された値をpx値に変換する
   * @param any 変換前の値
   * @param unit 変換前の値の単位
   */
  protected toPixel(any: number, unit: string): number {
    let rtn = any;
    if (any != null) {
      switch (unit) {
        case 'mm':
          // rtn(px値) = dpi ÷ inch × mm値
          rtn = this.dpi / this.mmPerInch * any;
          break;
        case 'cm':
          // rtn(px値) = dpi ÷ inch × (cm値 / 10)
          rtn = this.dpi / this.mmPerInch * (any / 10);
          break;
        case 'in':
          // rtn(px値) = inch値 × dpi
          rtn = any * this.dpi;
          break;
        case 'pt':
          // rtn(px値) = (pt値 ÷ 72) × dpi
          rtn = (any / 72) * this.dpi;
          break;
        default:
          // 他にもCSS上で指定可能な単位はいくつかあるが、ph2のタイミングでは上記のみ実装を入れておく
          break;
      }
      rtn = this.round(rtn);
    }
    return rtn;
  }

  /**
   * 単位を変換する
   * @param beforeValue 変換前の値
   * @param fromUnit 変換前の値の単位
   * @param toUnit 変換後の値の単位
   */
  protected convertUnit(beforeValue: number, fromUnit: string, toUnit: string): number {
    const afterValue = this.toPixel(beforeValue, fromUnit);
    return this.fromPixel(afterValue, toUnit);
  }

  /**
   * 小数点以下第3位を四捨五入して、小数点以下第2位までの値を返却する
   * @param value 変換前の値
   */
  protected round(value: number): number {
    const decimal = 1;
    return Math.round(value * Math.pow(10, decimal)) / Math.pow(10, decimal);
  }

  /**
   * プロパティ一覧のメタデータに対し、テンプレートJSONの値を設定する
   * @param bases プロパティ一覧のメタデータ
   * @param json テンプレートJSON
   */
  private updateMetadataByJSON(bases: PropertyPreviewMetadata[], json: TemplateJSON): PropertyPreviewMetadata[] {
    const typeOf = Object.prototype.toString;
    let textFieldType: FieldType;

    for (const base of bases) {

      // 後続の処理（inputTypeの設定値によるisFieldTypeの再設定）の事前評価
      if (base.name === 'inputType') {
        switch (json[base.name]) {
          case 'text':
          // textFieldType = 'Text';
          // break;
          case 'textArea':
            textFieldType = 'TextArea';
            break;
          case 'select':
          case 'radio':
            break;
        }
      }

      // base.valueプロパティの設定＋isFieldTypeの再設定
      switch (!base.parentName ? typeOf.call(json[base.name]) : null) {
        case '[object Object]':
        case '[object Array]':
          base.value = JSON.stringify(base.parentName ? json[base.parentName][base.name] : json[base.name], undefined, 4);
          // base.isFieldType = (type: FieldType) => type === 'JSON';
          break;

          // case '[object Null]':
          // case '[object Undefined]':
          //   base.value = '';
          //   break;

        default:
          if (!base.parentName) {
            base.value = json[base.name];
          } else {
            base.value = json[base.parentName] ? json[base.parentName][base.name] : null;
          }
          break;
      }
    }

    // inputTypeの設定値によるisFieldTypeの再設定
    for (const base of bases) {
      if (base.name === 'text' && textFieldType) {
        base.isFieldType = (type: FieldType) => type === textFieldType;
        break;
      }
    }

    return bases;
  }

  /**
   * UUIDを生成する
   * 流用ロジック：https://github.com/GoogleChrome/chrome-platform-analytics/blob/master/src/internal/identifier.js
   * ライセンス  ：Apache License v2.0
   */
  protected generateUUID(): string {
    const chars = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.split('');
    for (let i = 0, len = chars.length; i < len; i++) {
      switch (chars[i]) {
        case 'x':
          chars[i] = Math.floor(Math.random() * 16).toString(16);
          break;
        case 'y':
          chars[i] = (Math.floor(Math.random() * 4) + 8).toString(16);
          break;
      }
    }
    return chars.join('');
  }
}

/** *****************************************************************/
export class ReportUnits extends ReportProperties {

  public onUpdateFontSizeUnit: Observable<UpdateMetadata>;
  private onUpdateFontSizeUnit$: Subject<UpdateMetadata>;
  public onUpdateSizeUnit: Observable<UpdateMetadata>;
  private onUpdateSizeUnit$: Subject<UpdateMetadata>;

  private baseDef: TemplateDefinition;
  private readonly metaFontSizeUnit = 'fontSizeUnit';
  private readonly metaSizeUnit = 'sizeUnit';

  /**
   * コンストラクタ
   */
  constructor(templateDef: TemplateDefinition) {

    super('Size Unit');

    this.onUpdateFontSizeUnit$ = new Subject<UpdateMetadata>();
    this.onUpdateFontSizeUnit = this.onUpdateFontSizeUnit$.asObservable();
    this.onUpdateSizeUnit$ = new Subject<UpdateMetadata>();
    this.onUpdateSizeUnit = this.onUpdateSizeUnit$.asObservable();

    this.baseDef = templateDef;

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  /**
   * レポートプレビュー時に適用させるサイズ単位を注入した値を返却する
   */
  public concatenate(useUnit: 'fontSizeUnit' | 'sizeUnit', value: number): string {
    let rtn = '';
    if (value != null) {
      const tmp: Array<string> = [];
      tmp.push(value.toString());
      tmp.push(this.baseDef[useUnit]);
      rtn = tmp.join('');
    }
    return rtn;
  }

  /**
   * 自インスタンスが保持する単位のうち、指定された単位からpx単位の値を求める
   * @param useUnit 自インスタンスが保持する単位のうち、使用する単位
   * @param value 変換したい値
   */
  public toPx(useUnit: 'fontSizeUnit' | 'sizeUnit', value: number): number {
    return this.toPixel(value, this.baseDef[useUnit]);
  }

  /**
   * 自インスタンスが保持する単位のうち、px単位から指定された単位の値を求める
   * @param useUnit 自インスタンスが保持する単位のうち、使用する単位
   * @param value 変換したい値
   */
  public fromPx(useUnit: 'fontSizeUnit' | 'sizeUnit', value: number): number {
    return this.fromPixel(value, this.baseDef[useUnit]);
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplateDefinition {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaFontSizeUnit, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'px', value: 'px' },
          { label: 'mm', value: 'mm' },
          { label: 'cm', value: 'cm' },
          { label: 'in', value: 'in' },
          { label: 'pt', value: 'pt' },
        ], // ↑ 他にも選択肢を増やす場合は、ReportProperties.fromPixel(),.toPixel()に定義した計算式も併せて増やすこと。
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaSizeUnit, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'px', value: 'px' },
          { label: 'mm', value: 'mm' },
          { label: 'cm', value: 'cm' },
          { label: 'in', value: 'in' },
        ], // ↑ 他にも選択肢を増やす場合は、ReportProperties.fromPixel(),.toPixel()に定義した計算式も併せて増やすこと。
        isRequired: true,
        isEdited: false,
      }
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaFontSizeUnit:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        this.onUpdateFontSizeUnit$.next(metadata);
        break;
      case this.metaSizeUnit:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        this.onUpdateSizeUnit$.next(metadata);
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    // 他のオブジェクトが持つ値が更新されたことによって、自身が持つプロパティを更新するパターンは無いため、NOP
  }
}

/** *****************************************************************/
export class ReportFonts extends ReportProperties {

  public onUpdateFontSize: Observable<UpdateMetadata>;
  private onUpdateFontSize$: Subject<UpdateMetadata>;
  public onUpdateFontFamily: Observable<UpdateMetadata>;
  private onUpdateFontFamily$: Subject<UpdateMetadata>;

  private baseDef: TemplateDefinition;
  private units: ReportUnits;

  private readonly metaFontSize = 'fontSize';
  private readonly metaFontFamily = 'fontFamily';

  /**
   * コンストラクタ
   */
  constructor(templateDef: TemplateDefinition, units: ReportUnits) {

    super('Report Font');

    this.units = units;
    this.onUpdateFontSize$ = new Subject();
    this.onUpdateFontSize = this.onUpdateFontSize$.asObservable();
    this.onUpdateFontFamily$ = new Subject();
    this.onUpdateFontFamily = this.onUpdateFontFamily$.asObservable();

    // 読み取り元のJSONオブジェクトを内部で保持
    this.baseDef = templateDef;

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  /**
   * レポートプレビュー時に適用させるフォントサイズを返却する
   */
  public getFontSize(templateDef?: TemplateText): string {
    const rtn: string = (templateDef && templateDef.hasOwnProperty('fontSize') && templateDef.fontSize > 0) ?
      this.units.concatenate('fontSizeUnit', templateDef.fontSize) :
      this.units.concatenate('fontSizeUnit', this.baseDef.fontSize);
    return rtn;
  }

  /**
   * レポートプレビュー時に適用させるフォントを返却する
   */
  public getFontFamily(templateDef?: TemplateText): string {
    const rtn: string = (templateDef && templateDef.hasOwnProperty('fontFamily') && templateDef.fontFamily != null) ?
      templateDef.fontFamily :
      this.baseDef.fontFamily;
    return rtn;
  }

  /**
   * テンプレートJSOｎを返却する
   */
  public getTemplateJSON(): TemplateDefinition {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaFontSize, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaFontFamily, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'Times New Roman', value: 'Times New Roman' },
          { label: 'sans-serif', value: 'sans-serif' },
          { label: 'serif', value: 'serif' },
          { label: 'Arial', value: 'Arial' },
          { label: 'Calibri', value: 'Calibri' },
        ],
        isRequired: true,
        isEdited: false,
      }
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaFontSize:
        this.baseDef[metadata.name] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        this.onUpdateFontSize$.next(metadata);
        break;
      case this.metaFontFamily:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        this.onUpdateFontFamily$.next(metadata);
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.units.onUpdateFontSizeUnit.subscribe((metadata: UpdateMetadata) => {
        // フォントサイズの単位が変更された時、自身が持つフォントサイズの値を再計算する
        // 自身が持つフォントサイズのプロパティに新しい値を設定する＝未保存状態にする必要があるため
        // updateProperties()経由で新しい値を設定する
        this.updateProperties({
          id: this.getId(),
          name: this.metaFontSize,
          value: this.convertUnit(this.baseDef.fontSize, metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef.fontSize
        });
      })
    );
  }
}

/** *****************************************************************/
export class ReportPaperSize extends ReportProperties {

  public onUpdatePaperSize: Observable<UpdateMetadata>;
  private onUpdatePaperSize$: Subject<UpdateMetadata>;

  private baseDef: TemplateDefinition;
  private readonly metaPaperSize = 'paperSize';

  constructor(templateDef: TemplateDefinition) {

    super('Paper Size');

    this.onUpdatePaperSize$ = new Subject();
    this.onUpdatePaperSize = this.onUpdatePaperSize$.asObservable();

    // 読み取り元のJSONオブジェクトを内部で保持
    this.baseDef = templateDef;

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public getPaperSize(): string {
    return this.baseDef.paperSize;
  }

  /**
   * paperSizeに格納された用紙サイズ（規格名）から実際の幅値を返却する
   * 規格名からサイズ値を返却するため「mm単位の値」を返却する
   */
  public getWidthNum(): number {
    switch (this.baseDef.paperSize) {
      case 'A4':
        return 210;
      case 'B5':
        return 176;
      case 'B5JIS':
        return 182;
      case 'Letter':
      case 'Legal':
      default:
        return 215.9;
    }
  }
  public getWidth(): string {
    return [this.getWidthNum(), 'mm'].join(''); // 単位はmm固定
  }
  public getWidthPx(): number {
    return this.toPixel(this.getWidthNum(), 'mm'); // mm→pxの変換処理の結果を返却する
  }

  /**
   * paperSizeに格納された用紙サイズ（規格名）から実際の高さ値を返却する
   * 規格名からサイズ値を返却するため「mm単位の値」を返却する
   */
  public getHeightNum(): number {
    switch (this.baseDef.paperSize) {
      case 'A4':
        return 297;
      case 'B5':
        return 250;
      case 'B5JIS':
        return 257;
      case 'Legal':
        return 355.6;
      case 'Letter':
      default:
        return 279.4;
    }
  }
  public getHeight(): string {
    return [this.getHeightNum(), 'mm'].join(''); // 単位はmm固定
  }
  public getHeightPx(): number {
    return this.toPixel(this.getHeightNum(), 'mm'); // mm→pxの変換処理の結果を返却する
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplateDefinition {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  public getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaPaperSize, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'Letter', value: 'Letter' },
          { label: 'Legal', value: 'Legal' },
          { label: 'A4', value: 'A4' },
          { label: 'B5', value: 'B5' },
        ], // ↑ 他にも選択肢を増やす場合は、ReportPaperSize.getWidthNum(), .getHeightNum()の返却値パターンを増やすこと。
        isRequired: true,
        isEdited: false,
      }
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaPaperSize:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        this.onUpdatePaperSize$.next(metadata);
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    // 他のオブジェクトが持つ値が更新されたことによって、自身が持つプロパティを更新するパターンは無いため、NOP
    // ※規格名から値を決めるため、値の単位も規格から判断する＝テンプレートJSONに記録されている単位は用いない
  }
}

/** *****************************************************************/
export class ReportPageMargin extends ReportProperties {

  public onUpdateTop: Observable<UpdateMetadata>;
  private onUpdateTop$: Subject<UpdateMetadata>;
  public onUpdateBottom: Observable<UpdateMetadata>;
  private onUpdateBottom$: Subject<UpdateMetadata>;
  public onUpdateLeft: Observable<UpdateMetadata>;
  private onUpdateLeft$: Subject<UpdateMetadata>;
  public onUpdateRight: Observable<UpdateMetadata>;
  private onUpdateRight$: Subject<UpdateMetadata>;

  private baseDef: TemplateDefinition;
  private readonly metaTop = 'pageMarginTop';
  private readonly metaBottom = 'pageMarginBottom';
  private readonly metaLeft = 'pageMarginLeft';
  private readonly metaRight = 'pageMarginRight';

  constructor(templateDef: TemplateDefinition) {

    super('Page Margin');

    this.onUpdateTop$ = new Subject();
    this.onUpdateTop = this.onUpdateTop$.asObservable();
    this.onUpdateBottom$ = new Subject();
    this.onUpdateBottom = this.onUpdateBottom$.asObservable();
    this.onUpdateLeft$ = new Subject();
    this.onUpdateLeft = this.onUpdateLeft$.asObservable();
    this.onUpdateRight$ = new Subject();
    this.onUpdateRight = this.onUpdateRight$.asObservable();

    // 読み取り元のJSONオブジェクトを内部で保持
    this.baseDef = templateDef;

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public getTop(): string {
    // 単位はmm固定 ※用紙サイズとの引き算があるため
    return [this.baseDef.pageMarginTop.toString(), 'mm'].join('');
  }
  public getBottom(): string {
    // 単位はmm固定 ※用紙サイズとの引き算があるため
    return [this.baseDef.pageMarginBottom.toString(), 'mm'].join('');
  }
  public getLeft(): string {
    // 単位はmm固定 ※用紙サイズとの引き算があるため
    return [this.baseDef.pageMarginLeft.toString(), 'mm'].join('');
  }
  public getRight(): string {
    // 単位はmm固定 ※用紙サイズとの引き算があるため
    return [this.baseDef.pageMarginRight.toString(), 'mm'].join('');
  }

  public getTopNum(): number {
    return this.baseDef.pageMarginTop;
  }
  public getBottomNum(): number {
    return this.baseDef.pageMarginBottom;
  }
  public getLeftNum(): number {
    return this.baseDef.pageMarginLeft;
  }
  public getRightNum(): number {
    return this.baseDef.pageMarginRight;
  }

  public getPaddedWidthNum(value: number): number {
    const left = this.baseDef.pageMarginLeft;
    const right = this.baseDef.pageMarginRight;
    return value - (left + right);
  }
  public getPaddedWidth(value: number): string {
    const paddedWidth = [this.getPaddedWidthNum(value), 'mm'];
    return paddedWidth.join('');
  }

  public getPaddedHeightNum(value: number): number {
    const top = this.baseDef.pageMarginTop;
    const bottom = this.baseDef.pageMarginBottom;
    return value - (top + bottom);
  }
  public getPaddedHeight(value: number): string {
    const paddedHeight = [this.getPaddedHeightNum(value), 'mm'];
    return paddedHeight.join('');
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplateDefinition {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaBottom, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaRight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      }
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        this.onUpdateTop$.next(metadata);
        break;
      case this.metaBottom:
        this.baseDef[metadata.name] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        this.onUpdateBottom$.next(metadata);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        this.onUpdateLeft$.next(metadata);
        break;
      case this.metaRight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        this.onUpdateRight$.next(metadata);
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    // 他のオブジェクトが持つ値が更新されたことによって、自身が持つプロパティを更新するパターンは無いため、NOP
    // ※top, bottom, left, rightは、のちにPaperSizeのwidthやheightと引き算することになるためmmで固定としている
  }
}

/** *****************************************************************/
export class ReportCommonSection extends ReportProperties {

  public onUpdateHeight: Observable<UpdateMetadata>;
  private onUpdateHeight$: Subject<UpdateMetadata>;

  private baseDef: TemplateCommonSection;
  private readonly metaId = 'id';
  private readonly metaHeight = 'height';

  constructor(type: 'Page Header' | 'Page Footer', templateDef: TemplateCommonSection) {

    super(type, templateDef ? templateDef.id : null);

    this.onUpdateHeight$ = new Subject();
    this.onUpdateHeight = this.onUpdateHeight$.asObservable();

    // 読み取り元のJSONオブジェクトを内部で保持
    this.baseDef = templateDef;

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public getHeightNum(): number {
    return this.baseDef.height;
  }
  public getHeight(): string {
    return [this.getHeightNum(), 'mm'].join('');
  }
  public getHeightPx(): number {
    return this.toPixel(this.getHeightNum(), 'mm');
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplateCommonSection {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      // {
      //   name: this.metaId,parentName: null, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'Text',
      //   selectItems: [],
      //   isRequired: false,
      //   isEdited: false,
      // },
      {
        name: this.metaHeight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      }
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        this.onUpdateHeight$.next(metadata);
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    // 他のオブジェクトが持つ値が更新されたことによって、自身が持つプロパティを更新するパターンは無いため、NOP
    // ※heightは、のちにPaperSizeのheightと引き算することになるためmmで固定としている
  }
}

/** *****************************************************************/
export abstract class ReportElement extends ReportProperties {

  isSelected: boolean;
  isSelectable: boolean;

  private index: number;
  private location: Location;
  private pageId: string;
  private pageIndex: number;

  /**
   * コンストラクタ
   */
  constructor(type: ElementType, index: number, isExisting: boolean, id?: string) {
    super(type, id);
    this.index = index;
    this.isSelected = false;
    this.isSelectable = true;
    this.setExistState(isExisting);
    this.setSaveState(isExisting); // 既存JSON定義をインスタンス化した直後なら、isExistingが示す状態と同じはず。
  }

  public abstract setDrawData(metadata: ReportPreviewMetadata);
  public abstract setDropData(metadata: ReportPreviewMetadata);
  public abstract setResizeData(metadata: ReportPreviewMetadata);
  public abstract getLeftPx(): number;
  public abstract getTopPx(): number;
  public abstract getWidthPx(): number;
  public abstract getHeightPx(): number;

  /**
   * 自インスタンスが配置された領域を設定する
   * @param location ロケーション
   * @param pageId ページID（省略可能）
   * @param pageIndex ページ番号（省略可能）
   */
  public setLocation(location: Location, pageId?: string, pageIndex?: number) {
    this.location = location;
    this.pageId = pageId;
    this.pageIndex = pageIndex;
  }

  /**
   * 自インスタンスのインデックス番号を返却する
   */
  public getIndex(): number {
    return this.index;
  }
  public setIndex(value: number) {
    this.index = value;
  }

  /**
   * 自インスタンスがページ上に配置されている要素の場合、そのページのIDを返却する
   */
  public getPageId(): string {
    return this.pageId;
  }

  /**
   * 自インスタンスが配置されている領域を返却する
   */
  public getLocation(): Location {
    return this.location;
  }

  /**
   * 自インスタンスのElementTypeを返却する
   */
  public getElementType(): ElementType {
    return ElementType.cast(this.getType());
  }

  /**
   * Override
   * プロパティ一覧に表示する自インスタンスのタイトルを返却する
   */
  public getTitle(): string {
    const title = new Array<string>();
    title.push(this.type);

    switch (this.type) {
      case 'Page':
        title.push((this.index + 1).toString());
        break;
      default:
        title.push((this.index + 1).toString());
        title.push('(');
        switch (this.location) {
          case 'Header':
          case 'Footer':
            title.push(this.location);
            break;
          case 'Body':
            title.push('Page.' + (this.pageIndex + 1));
            title.push(this.location);
            break;
        }
        title.push(')');
        break;
    }

    return title.join(' ').trim();
  }
}

/** *****************************************************************/
export class ReportPage extends ReportElement {

  // Angularオートバインディングプロパティ
  public isDrawMode: boolean;
  public hasHeader: boolean;
  public hasFooter: boolean;
  public paperWidth: string;
  public paperHeight: string;
  public bodyHeight: string;
  public headerHeight: string;
  public footerHeight: string;
  public paddingTop: string;
  public paddingBottom: string;
  public paddingLeft: string;
  public paddingRight: string;
  public paperSize: ReportPaperSize;

  // 関連インスタンス
  private header: ReportCommonSection;
  private footer: ReportCommonSection;
  private pageMargin: ReportPageMargin;

  // テンプレートJSONに関するプロパティ
  private baseDef: TemplatePageSection;
  private readonly metaId = 'id';
  private readonly metaHeight = 'height';
  private readonly metaHasHeader = 'hasHeader';
  private readonly metaHasFooter = 'hasFooter';

  /**
   * コンストラクタ
   * @param index ReportPageの基となるTemplateJSON側のPage定義配列のインデックス値
   * @param sizeUnits ReportPageが持つプロパティにサイズ単位を与えるために必要
   * @param header ReportPage（Bodyセクション）の高さを求めるときに必要
   * @param footer ReportPage（Bodyセクション）の高さを求めるときに必要
   * @param pageSize ReportPage自身の高さ、幅を求めるときに必要
   * @param pageMargin ReportPage自身の余白を求めるときに必要
   * @param templateBody TemplateJSON側の定義情報。省略可能。
   */
  constructor(
    index: number,
    header: ReportCommonSection, footer: ReportCommonSection,
    paperSize: ReportPaperSize, pageMargin: ReportPageMargin,
    templateBody?: TemplatePageSection
  ) {

    super(ElementType.Page, index, !(templateBody == null));

    this.header = header;
    this.footer = footer;
    this.paperSize = paperSize;
    this.pageMargin = pageMargin;

    if (templateBody) {
      this.baseDef = templateBody;

    } else {
      this.baseDef = {
        id: this.generateUUID(),
        height: 0,
        hasHeader: true, // 初期値をtrueで仮設定
        hasFooter: true, // 初期値をtrueで仮設定
        texts: [], // 初期値を空配列で仮設定
        images: [], // 初期値を空配列で仮設定
        lines: [], // 初期値を空配列で仮設定
        rectangles: [], // 初期値を空配列で仮設定
        inspectionSummary: null,
        inspectionPictures: null,
        dataPlates: null,
      };
    }
    // テンプレートJSON「bodies配列」のheightは、用紙サイズとMarginの値から計算で求める
    // this.baseDef.height = this.getBodyHeightNum();

    this.isDrawMode = false;
    this.hasHeader = this.baseDef.hasHeader;
    this.hasFooter = this.baseDef.hasFooter;
    this.paperWidth = paperSize.getWidth();
    this.paperHeight = paperSize.getHeight();
    this.paddingTop = pageMargin.getTop();
    this.paddingBottom = pageMargin.getBottom();
    this.paddingLeft = pageMargin.getLeft();
    this.paddingRight = pageMargin.getRight();
    this.headerHeight = header.getHeight();
    this.footerHeight = footer.getHeight();
    this.bodyHeight = this.getBodyHeight();

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    // NOP
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    // NOP
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    // NOP
  }

  public getAnchorId(): string {
    return 'p' + (this.getIndex() + 1);
  }

  public getTopPx(): number {
    return 0; // NOP
  }

  public getLeftPx(): number {
    return 0; // NOP
  }

  public getHeight(location: Location): string {
    let rtn: string;
    switch (location) {
      case Location.Header:
        rtn = this.header.getHeight();
        break;
      case Location.Body:
        rtn = this.getBodyHeight();
        break;
      case Location.Footer:
        rtn = this.footer.getHeight();
        break;
      case Location.Page:
        rtn = this.paperSize.getHeight();
        break;
    }
    return rtn;
  }

  public getHeightNum(location: Location): number {
    let rtn: number;
    switch (location) {
      case Location.Header:
        rtn = this.header.getHeightNum();
        break;
      case Location.Body:
        rtn = this.getBodyHeightNum();
        break;
      case Location.Footer:
        rtn = this.footer.getHeightNum();
        break;
      case Location.Page:
        rtn = this.paperSize.getHeightNum();
        break;
    }
    return rtn;
  }

  public getHeightPx(location?: Location): number {

    if (!location) { return 0; }

    let rtn: number;

    switch (location) {
      case Location.Header:
        rtn = this.header.getHeightPx();
        break;
      case Location.Body:
        rtn = this.getBodyHeightPx();
        break;
      case Location.Footer:
        rtn = this.footer.getHeightPx();
        break;
      case Location.Page:
        rtn = this.paperSize.getHeightPx();
        break;
    }
    return rtn;
  }

  public getWidth(location: Location): string {
    let rtn: string;
    switch (location) {
      case Location.Header:
      case Location.Body:
      case Location.Footer:
        rtn = this.getCoreWidth();
        break;
      case Location.Page:
        rtn = this.paperSize.getWidth();
        break;
    }
    return rtn;
  }

  public getWidthNum(location: Location): number {
    let rtn: number;
    switch (location) {
      case Location.Header:
      case Location.Body:
      case Location.Footer:
        rtn = this.getCoreWidthNum();
        break;
      case Location.Page:
        rtn = this.paperSize.getWidthNum();
        break;
    }
    return rtn;
  }

  public getWidthPx(location?: Location): number {
    if (!location) { return 0; }

    let rtn: number;
    switch (location) {
      case Location.Header:
      case Location.Body:
      case Location.Footer:
        rtn = this.getCoreWidthPx();
        break;
      case Location.Page:
        rtn = this.paperSize.getWidthPx();
        break;
    }
    return rtn;
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplatePageSection {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      // {
      //   name: this.metaId,parentName: null, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'Text',
      //   selectItems: [],
      //   isRequired: false,
      //   isEdited: false,
      // },
      // {
      //   name: this.metaHeight,parentName: null, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'ReadOnly',
      //   selectItems: [],
      //   isRequired: false,
      //   isEdited: false,
      // },
      {
        name: this.metaHasHeader, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaHasFooter, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        this.bodyHeight = this.getBodyHeight();
        break;
      case this.metaHasHeader:
        this.baseDef[metadata.name] = metadata.value as boolean;
        // this.baseDef[this.metaHeight] = this.getBodyHeightNum();
        this.setPreviewValue(metadata.name, metadata.value);
        // this.setPreviewValue(this.metaHeight, this.getBodyHeightNum());
        this.hasHeader = metadata.value as boolean;
        // this.bodyHeight = this.getBodyHeight();
        break;
      case this.metaHasFooter:
        this.baseDef[metadata.name] = metadata.value as boolean;
        // this.baseDef[this.metaHeight] = this.getBodyHeightNum();
        this.setPreviewValue(metadata.name, metadata.value);
        // this.setPreviewValue(this.metaHeight, this.getBodyHeightNum());
        this.hasFooter = metadata.value as boolean;
        // this.bodyHeight = this.getBodyHeight();
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.paperSize.onUpdatePaperSize.subscribe((metadata: UpdateMetadata) => {
        // paperWidth、paperHeightの再設定
        // ※ReportPageが保持するJSON側に上記２プロパティは無いため、updateProperties()は実行しない
        this.paperWidth = this.paperSize.getWidth();
        this.paperHeight = this.paperSize.getHeight();
        // bodyHeightの再設定
        // →Heightプロパティの値が変わる＝要Saveな状態にする必要があるので、
        //  updateProperties()経由でプロパティを設定
        // this.updateProperties({
        //   id: this.getId(),
        //   name: this.metaHeight,
        //   value: this.getBodyHeightNum(),
        //   oldValue: this.baseDef.height
        // });
      })
    );
    subscribers.push(
      this.pageMargin.onUpdateTop.subscribe((metadata: UpdateMetadata) => {
        // paddingTopの再設定
        // ※ReportPageが保持するJSON側に上記プロパティは無いため、updateProperties()は実行しない
        this.paddingTop = this.pageMargin.getTop();
        this.bodyHeight = this.getBodyHeight();
        // bodyHeightの再設定
        // this.updateProperties({
        //   id: this.getId(),
        //   name: this.metaHeight,
        //   value: this.getBodyHeightNum(),
        //   oldValue: this.baseDef.height
        // });
      })
    );
    subscribers.push(
      this.pageMargin.onUpdateBottom.subscribe((metadata: UpdateMetadata) => {
        // paddingBottomの再設定
        // ※ReportPageが保持するJSON側に上記プロパティは無いため、updateProperties()は実行しない
        this.paddingBottom = this.pageMargin.getBottom();
        this.bodyHeight = this.getBodyHeight();
        // bodyHeightの再設定
        // this.updateProperties({
        //   id: this.getId(),
        //   name: this.metaHeight,
        //   value: this.getBodyHeightNum(),
        //   oldValue: this.baseDef.height
        // });
      })
    );
    subscribers.push(
      this.pageMargin.onUpdateLeft.subscribe((metadata: UpdateMetadata) => {
        // paddingLeftの再設定
        // ※ReportPageが保持するJSON側に上記プロパティは無いため、updateProperties()は実行しない
        this.paddingLeft = this.pageMargin.getLeft();
      })
    );
    subscribers.push(
      this.pageMargin.onUpdateRight.subscribe((metadata: UpdateMetadata) => {
        // paddingRightの再設定
        // ※ReportPageが保持するJSON側に上記プロパティは無いため、updateProperties()は実行しない
        this.paddingRight = this.pageMargin.getRight();
      })
    );
    subscribers.push(
      this.header.onUpdateHeight.subscribe((metadata: UpdateMetadata) => {
        // headerHeightの再設定
        // ※ReportPageが保持するJSON側に上記プロパティは無いため、updateProperties()は実行しない
        this.headerHeight = this.header.getHeight();
        // bodyHeightの再設定
        // this.updateProperties({
        //   id: this.getId(),
        //   name: this.metaHeight,
        //   value: this.getBodyHeightNum(),
        //   oldValue: this.baseDef.height
        // });
      })
    );
    subscribers.push(
      this.footer.onUpdateHeight.subscribe((metadata: UpdateMetadata) => {
        // footerHeightの再設定
        // ※ReportPageが保持するJSON側に上記プロパティは無いため、updateProperties()は実行しない
        this.footerHeight = this.footer.getHeight();
        // bodyHeightの再設定
        // this.updateProperties({
        //   id: this.getId(),
        //   name: this.metaHeight,
        //   value: this.getBodyHeightNum(),
        //   oldValue: this.baseDef.height
        // });
      })
    );
  }

  private getBodyHeightNum(): number {
    // 用紙サイズのHeightを取得
    // 用紙サイズのheightから、marginTop, marginBottomの値を引く
    // headerを表示するページの場合、headerのheightを引く
    // footerを表示するページの場合、footerのheightを引く
    // 得られた値を小数点以下第2位を四捨五入した値にして返却
    let rtn = this.paperSize.getHeightNum();
    rtn = this.pageMargin.getPaddedHeightNum(rtn);
    if (this.baseDef.hasHeader) {
      rtn = rtn - this.header.getHeightNum();
    }
    if (this.baseDef.hasFooter) {
      rtn = rtn - this.footer.getHeightNum();
    }
    rtn = this.round(rtn);
    return rtn;
  }
  private getBodyHeight(): string {
    return [this.getBodyHeightNum(), 'mm'].join('');
  }
  private getBodyHeightPx(): number {
    return this.toPixel(this.getBodyHeightNum(), 'mm');
  }

  private getCoreWidthNum(): number {
    // 用紙サイズのWidthを取得
    // 用紙サイズのWidthから、marginLeft, marginRightの値を引く
    // 得られた値を小数点以下第2位を四捨五入した値にして返却
    let rtn = this.paperSize.getWidthNum();
    rtn = this.pageMargin.getPaddedWidthNum(rtn);
    rtn = this.round(rtn);
    return rtn;
  }
  private getCoreWidth(): string {
    return [this.getCoreWidthNum(), 'mm'].join('');
  }
  private getCoreWidthPx(): number {
    return this.toPixel(this.getCoreWidthNum(), 'mm');
  }

}

/** *****************************************************************/
export class ReportText extends ReportElement {

  // Angularオートバインディングプロパティ
  public top: string;
  public left: string;
  public width: string;
  public height: string;
  public zIndex: number;
  public padding: string;
  public paddingLeft: string;
  public paddingBottom: string;
  public paddingRight: string;
  public paddingTop: string;
  public borderStyle: string;
  public borderWidth: number;
  public borderColor: string;
  public backgroundColor: string;
  public text: string;
  public fontSize: string;
  public fontFamily: string;
  public fontStyle: string;
  public fontWeight: string;
  public color: string;
  public textAlign: string;
  public verticalAlign: string;
  public isHorizontal: boolean;
  public lineHeight: string;
  public justifyContent: string; // JSONにはないプロパティ
  public alignItems: string; // JSONにはないプロパティ

  // 関連インスタンス
  private units: ReportUnits;
  private fonts: ReportFonts;

  // テンプレートJSONに関するプロパティ
  private baseDef: TemplateText;
  private readonly metaId = 'id';
  private readonly metaTop = 'y'; // JSONのプロパティ名とCSSのプロパティ名に差異があるのであえてこの変数名にしている
  private readonly metaLeft = 'x'; // JSONのプロパティ名とCSSのプロパティ名に差異があるのであえてこの変数名にしている
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaBorderStyle = 'borderStyle';
  private readonly metaBorderWidth = 'borderWidth';
  private readonly metaBorderColor = 'borderColor';
  private readonly metaBGColor = 'backgroundColor';
  private readonly metaType = 'type';
  private readonly metaText = 'text';
  private readonly metaFormat = 'format';
  private readonly metaFontSize = 'fontSize';
  private readonly metaFontFamily = 'fontFamily';
  private readonly metaFontColor = 'fontColor';
  private readonly metaFontStyle = 'fontStyle';
  private readonly metaFontWeight = 'fontWeight';
  private readonly metaTextAlign = 'horizontalAlign';
  private readonly metaVerticalAlign = 'verticalAlign';
  private readonly metaIsHorizontal = 'isHorizontal';
  private readonly metaLineHeight = 'lineHeight';
  private readonly metaDynamicId = 'dynamicId';
  private readonly metaInputType = 'inputType';
  private readonly metaEditableId = 'editableId';
  private readonly metaValidation = 'validation';
  private readonly metaRequired = 'required';
  private readonly metaMaxLength = 'maxLength';
  private readonly metaRadioType = 'radioType';
  private readonly metaRadioLabel = 'radioLabel';
  private readonly metaOptions = 'options';
  private readonly metaLabel = 'label';
  private readonly metaValue = 'value';
  private readonly metaInputGroup = 'inputGroup';

  /**
   * コンストラクタ
   */
  constructor(index: number, units: ReportUnits, fonts: ReportFonts, templateText?: TemplateText) {

    super(ElementType.Text, index, !(templateText == null), templateText ? templateText.id : null);

    this.units = units;
    this.fonts = fonts;

    if (templateText) {
      this.baseDef = templateText;

    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: null,
        y: null,
        width: null,
        height: null,
        zIndex: null,
        borderStyle: null,
        borderWidth: null,
        borderColor: null,
        backgroundColor: null,
        type: 'static', // 初期値をstaticで仮設定
        text: '',
        format: null,
        fontSize: null,
        fontFamily: null,
        fontColor: null,
        fontStyle: null,
        fontWeight: null,
        horizontalAlign: 'left', // 初期値をleftで仮設定
        verticalAlign: 'center', // 初期値をcenterで仮設定
        isHorizontal: true,
        lineHeight: null,
        dynamicDataField: null, // 初期値をnullで仮設定
        dynamicId: null, // 初期値をnullで仮設定
        inputType: 'text', // 初期値をtextで仮設定
        editableId: null,
        validation: {
          required: false,
          maxLength: 100,
        },
        radioType: null,
        radioLabel: null,
        options: [],
        label: null,
        value: null,
        inputGroup: null,
        padding: null,
        paddingTop: 0.5,
        paddingLeft: 0.5,
        paddingBottom: 0.5,
        paddingRight: 0.5,
        multiSelectDelimiter: 'comma',
        isTableText: false
      };
    }

    this.zIndex = this.baseDef.zIndex;
    this.borderStyle = this.baseDef.borderStyle;
    this.borderWidth = this.baseDef.borderWidth;
    this.borderColor = this.baseDef.borderColor;
    this.backgroundColor = this.baseDef.backgroundColor;
    this.fontStyle = this.baseDef.fontStyle;
    this.fontWeight = this.baseDef.fontWeight;
    this.color = this.baseDef.fontColor;
    this.textAlign = this.baseDef.horizontalAlign;
    this.verticalAlign = this.baseDef.verticalAlign;
    this.isHorizontal = this.baseDef.isHorizontal;

    this.top = this.units.concatenate('sizeUnit', this.baseDef.y);
    this.left = this.units.concatenate('sizeUnit', this.baseDef.x);
    this.width = this.units.concatenate('sizeUnit', this.baseDef.width);
    this.height = this.units.concatenate('sizeUnit', this.baseDef.height);
    this.lineHeight = this.units.concatenate('sizeUnit', this.baseDef.lineHeight);
    this.fontSize = this.fonts.getFontSize(this.baseDef);
    this.fontFamily = this.fonts.getFontFamily(this.baseDef);

    this.paddingLeft = this.units.concatenate('sizeUnit', this.baseDef.paddingLeft);
    this.paddingBottom = this.units.concatenate('sizeUnit', this.baseDef.paddingBottom);
    this.paddingRight = this.units.concatenate('sizeUnit', this.baseDef.paddingRight);
    this.paddingTop = this.units.concatenate('sizeUnit', this.baseDef.paddingTop);

    this.text = this.getText();
    this.justifyContent = this.getJustifyContent();
    this.alignItems = this.getAlignItems();

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public isGuideMarkType(type: 'editable' | 'static' | 'dynamic'): boolean {
    let rtn = false;
    switch (type) {
      case 'editable':
        rtn = (this.baseDef.type === 'dynamic-editable');
        break;
      case 'static':
        rtn = (this.baseDef.type === 'static');
        break;
      case 'dynamic':
        rtn = (this.baseDef.type === 'dynamic');
        break;
    }
    // console.log('ReportText(%s) isGuideMarkType End.', this.getId());
    return rtn;
  }

  public setDrawData(metadata: ReportPreviewMetadata) {

    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }

  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaHeight]);
  }
  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaWidth]);
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplateText {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      // {
      //   name: this.metaId,parentName: null, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'Text',
      //   selectItems: [],
      //   isRequired: false,
      //   isEdited: false,
      // },
      {
        name: this.metaLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaHeight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaType, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'static', value: 'static' },
          { label: 'dynamic', value: 'dynamic' },
          { label: 'dynamic-editable', value: 'dynamic-editable' },
        ],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaInputType, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'text', value: 'text' },
          { label: 'textArea', value: 'textArea' },
          { label: 'radio', value: 'radio' },
          { label: 'select', value: 'select' },
          { label: 'date', value: 'date' },
        ],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaText, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'TextArea',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaDynamicId, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: DYNAMIC_SELECT_ITEM,
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaEditableId, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaFormat, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: '01/01/2019', value: 'MM/dd/yyyy' },
          { label: '1/1/2019', value: 'M/d/yyyy' },
          { label: '1/1/19', value: 'M/d/yy' },
          { label: '2019/1/1', value: 'yyyy/M/d' },
          { label: '19/1/1', value: 'yy/M/d' },
          { label: '2019-01-01', value: 'yyyy-MM-dd' },
          { label: '2019-1-1', value: 'yyyy-M-d' },
          { label: '19-01-01', value: 'yy-MM-dd' },
          { label: 'January 1, 2019', value: 'MMMM d, yyyy' },
          { label: 'January 01, 2019', value: 'MMMM dd, yyyy' },
          { label: 'Tuesday, January 01, 2019', value: 'dddd, MMMM dd, yyyy' },
          { label: 'Tuesday, Jan 1, 2019', value: 'dddd, MMM d, yyy' },
          { label: 'Tues, Jan 1, 2019', value: 'ddd, MMM d, yyyy' },
          { label: '1/10', value: '$/#' },
          { label: '1', value: '$' },
          { label: 'p.1', value: 'p.$' },
          { label: 'Page 1 of 10', value: 'Page $ of #' },
        ],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaMaxLength, parentName: this.metaValidation, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaOptions, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Multi',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaRadioType, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'check', value: 'check' },
          { label: 'circle', value: 'circle' },
          { label: 'disc', value: 'disc' },
        ],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaRadioLabel, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaValue, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaZIndex, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaBorderStyle, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'none', value: 'none' },
          { label: 'hidden', value: 'hidden' },
          { label: 'solid', value: 'solid' },
          { label: 'double', value: 'double' },
          { label: 'groove', value: 'groove' },
          { label: 'ridge', value: 'ridge' },
          { label: 'inset', value: 'inset' },
          { label: 'outset', value: 'outset' },
          { label: 'dashed', value: 'dashed' },
          { label: 'dotted', value: 'dotted' },
        ],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaBorderWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaBorderColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaBGColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaFontSize, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaFontFamily, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'Times New Roman', value: 'Times New Roman' },
          { label: 'sans-serif', value: 'sans-serif' },
          { label: 'serif', value: 'serif' },
          { label: 'Arial', value: 'Arial' },
          { label: 'Calibri', value: 'Calibri' },
        ],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaFontColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaFontStyle, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'italic', value: 'italic' },
          { label: 'oblique', value: 'oblique' },
        ],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaFontWeight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'normal', value: 'normal' },
          { label: 'bold', value: 'bold' },
          { label: 'lighter', value: 'lighter' },
          { label: 'bolder', value: 'bolder' },
          { label: '100', value: '100' },
          { label: '200', value: '200' },
          { label: '300', value: '300' },
          { label: '400', value: '400' },
          { label: '500', value: '500' },
          { label: '600', value: '600' },
          { label: '700', value: '700' },
          { label: '800', value: '800' },
          { label: '900', value: '900' },
        ],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaTextAlign, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'left', value: 'left' },
          { label: 'center', value: 'center' },
          { label: 'right', value: 'right' },
        ],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaVerticalAlign, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'top', value: 'top' },
          { label: 'middle', value: 'middle' },
          { label: 'bottom', value: 'bottom' },
        ],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaLineHeight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        // ↑ CSSプロパティ「lineHight」は文字列値も受け付けられたような。。。
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaRequired, parentName: this.metaValidation, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaLabel, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaInputGroup, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.name] = metadata.value as number;
        this.zIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderStyle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.borderWidth = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBGColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.backgroundColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaType:
        this.baseDef[metadata.name] = metadata.value as string;
        this.text = this.getText();
        this.setPreviewValue(metadata.name, metadata.value);
        if (metadata.value === 'static') {
          this.setPreviewValue(this.metaDynamicId, null);
          this.baseDef[this.metaDynamicId] = null;
          this.setPreviewValue(this.metaEditableId, null);
          this.baseDef[this.metaEditableId] = null;
          this.setPreviewValue(this.metaFormat, null);
          this.baseDef[this.metaFormat] = null;
        } else if (metadata.value === 'dynamic') {
          this.setPreviewValue(this.metaText, '');
          this.baseDef[this.metaText] = '';
          this.setPreviewValue(this.metaEditableId, null);
          this.baseDef[this.metaEditableId] = null;
        }
        break;
      case this.metaText:
        this.baseDef[metadata.name] = metadata.value as string;
        this.text = this.getText();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFormat:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontSize:
        this.baseDef[metadata.name] = metadata.value as number;
        this.fontSize = this.fonts.getFontSize(this.baseDef);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontFamily:
        this.baseDef[metadata.name] = metadata.value as string;
        this.fontFamily = this.fonts.getFontFamily(this.baseDef);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.color = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.fontStyle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontWeight:
        this.baseDef[metadata.name] = metadata.value as string;
        this.fontWeight = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTextAlign:
        this.baseDef[metadata.name] = metadata.value as string;
        this.textAlign = metadata.value as string;
        this.justifyContent = this.getJustifyContent();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaVerticalAlign:
        this.baseDef[metadata.name] = metadata.value as string;
        this.verticalAlign = metadata.value as string;
        this.alignItems = this.getAlignItems();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLineHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.lineHeight = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaDynamicId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.text = this.getText();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaInputType:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        if (metadata.value !== 'select') {
          this.setPreviewValue(this.metaOptions, '[]');
          this.baseDef[this.metaOptions] = [];
        }
        if (metadata.value !== 'radio') {
          this.setPreviewValue(this.metaRadioType, null);
          this.baseDef[this.metaRadioType] = null;
          this.setPreviewValue(this.metaRadioLabel, null);
          this.baseDef[this.metaRadioLabel] = null;
          this.setPreviewValue(this.metaValue, null);
          this.baseDef[this.metaValue] = null;
        }
        break;
      case this.metaEditableId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.text = this.getText();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      // case this.metaValidation:
      //   this.baseDef[metadata.name] = JSON.parse(metadata.value as string);
      //   this.setPreviewValue(metadata.name, metadata.value);
      //   break;
      case this.metaRequired:
        if (!this.baseDef[this.metaValidation]) {
          this.baseDef[this.metaValidation] = { required: false, maxLength: null };
        }
        this.baseDef[this.metaValidation][metadata.name] = metadata.value as boolean;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaMaxLength:
        if (!this.baseDef[this.metaValidation]) {
          this.baseDef[this.metaValidation] = { required: false, maxLength: null };
        }
        this.baseDef[this.metaValidation][metadata.name] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaRadioType:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaRadioLabel:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaOptions:
        this.baseDef[metadata.name] = JSON.parse(metadata.value as string);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLabel:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaValue:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaInputGroup:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {

    subscribers.push(
      this.units.onUpdateFontSizeUnit.subscribe((metadata: UpdateMetadata) => {
        // フォントサイズの単位が変更された時、自身が持つフォントサイズの値を再計算する
        // 自身が持つフォントサイズのプロパティに新しい値を設定する＝未保存状態にする必要があるため
        // updateProperties()経由で新しい値を設定する
        this.updateProperties({
          id: this.getId(),
          name: this.metaFontSize,
          value: this.convertUnit(this.baseDef[this.metaFontSize], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef.fontSize
        });
      })
    );

    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        // サイズの単位が変更された時、自身が持つサイズの値を再計算する
        // 自身が持つサイズのプロパティに新しい値を設定する＝未保存状態にする必要があるため
        // updateProperties()経由で新しい値を設定する
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.convertUnit(this.baseDef[this.metaWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaWidth]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaHeight,
          value: this.convertUnit(this.baseDef[this.metaHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaHeight]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLineHeight,
          value: this.convertUnit(this.baseDef[this.metaLineHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLineHeight]
        });
      })
    );

    subscribers.push(
      this.fonts.onUpdateFontSize.subscribe((metadata: UpdateMetadata) => {
        // レポート全体のフォントサイズが変更された時、自身が持つフォントサイズ値とレポート全体のフォントサイズ値どちらかを適用させる。
        // 自身が持つフォントサイズのプロパティに新しい値を設定するわけではないので、updateProperties()は経由しない
        this.fontSize = this.fonts.getFontSize(this.baseDef);
      })
    );

    subscribers.push(
      this.fonts.onUpdateFontFamily.subscribe((metadata: UpdateMetadata) => {
        // レポート全体のフォントが変更された時、自身が持つフォントとレポート全体のフォントどちらかを適用させる。
        // 自身が持つフォントのプロパティに新しい値を設定するわけではないので、updateProperties()は経由しない
        this.fontFamily = this.fonts.getFontFamily(this.baseDef);
      })
    );

  }

  private getText(): string {
    let rtnText = '';
    switch (this.baseDef.type) {
      case 'static':
        // TODO: this.baseDef.formatプロパティに定められた変換処理が必要
        rtnText = this.baseDef.text;
        break;
      case 'dynamic':
        // 仮の表示。サーバーからdynamic文字列が取得できるようなAPIが用意されたら、その文字列を返却する
        rtnText = this.baseDef.dynamicId ? '[' + this.baseDef.dynamicId + ']' : '[Dynamic Text]';
        break;
      case 'dynamic-editable':
        // Editableな項目は、レポート出力前に文字を設定してもらうため、デザインエディタ画面で表示する時に値は決まってない。
        rtnText = this.baseDef.editableId ? '{' + this.baseDef.editableId + '}' : '{Editable Text}';
        break;
    }
    return rtnText;
  }

  private getJustifyContent(): string {
    let rtn: string;
    switch (this.baseDef.horizontalAlign) {
      case 'left':
        rtn = 'flex-start';
        break;
      case 'center':
        rtn = 'center';
        break;
      case 'right':
        rtn = 'flex-end';
        break;
    }
    return rtn;
  }

  private getAlignItems(): string {
    let rtn: string;
    switch (this.baseDef.verticalAlign) {
      case 'top':
        rtn = 'flex-start';
        break;
      case 'middle':
        rtn = 'center';
        break;
      case 'bottom':
        rtn = 'flex-end';
        break;
    }
    return rtn;
  }
}

/** *****************************************************************/
export class ReportLine extends ReportElement {

  // Angularオートバインディングプロパティ
  public top: string;
  public left: string;
  public zIndex: number;
  public width: string;
  public height: string;
  public borderTopStyle: string;
  public borderTopColor: string;
  public borderLeftStyle: string;
  public borderLeftColor: string;

  // 関連インスタンス
  private units: ReportUnits;

  // テンプレートJSONに関するプロパティ
  private baseDef: TemplateLine;
  private readonly metaId = 'id';
  private readonly metaTop = 'y'; // JSONのプロパティ名とCSSのプロパティ名に差異があるのであえてこの変数名にしている
  private readonly metaLeft = 'x'; // JSONのプロパティ名とCSSのプロパティ名に差異があるのであえてこの変数名にしている
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaBorderStyle = 'borderStyle';
  private readonly metaBorderColor = 'borderColor';
  private readonly metaLength = 'length';
  private readonly metaIsHorizontal = 'isHorizontal';

  /**
   * コンストラクタ
   * @param index ReportLineの基となるTemplateJSON側のLine定義配列のインデックス値
   * @param units ReportLineが持つプロパティにサイズ単位を与えるために必要
   * @param templateLine TemplateJSON側の定義情報。省略可能
   */
  constructor(index: number, units: ReportUnits, templateLine?: TemplateLine) {

    super(ElementType.Line, index, !(templateLine == null), templateLine ? templateLine.id : null);

    this.units = units;

    if (templateLine) {
      this.baseDef = templateLine;

    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0, // 初期値を0で仮設定
        y: 0, // 初期値を0で仮設定
        width: 0, // 初期値を0で仮設定
        height: 0, // 初期値を0で仮設定
        zIndex: 0, // 初期値を0で仮設定
        borderStyle: 'solid', // 初期値をsolidで仮設定
        borderColor: '#a0a0a0', // 初期値を#a0a0a0で仮設定
        length: 10, // 初期値を0で仮設定
        isHorizontal: true // 初期値をtrueで仮設定
      };
    }
    this.zIndex = this.baseDef.zIndex;

    this.top = this.units.concatenate('sizeUnit', this.baseDef.y);
    this.left = this.units.concatenate('sizeUnit', this.baseDef.x);

    this.width = this.getWidth();
    this.height = this.getHeight();
    this.borderTopStyle = this.getBorderTopStyle();
    this.borderTopColor = this.getBorderTopColor();
    this.borderLeftStyle = this.getBorderLeftStyle();
    this.borderLeftColor = this.getBorderLeftColor();

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    let lineLength: number;
    let lineIsHorizontal: boolean;
    if (metadata.widthPx < metadata.heightPx) {
      lineLength = metadata.heightPx;
      lineIsHorizontal = false;
    } else {
      lineLength = metadata.widthPx;
      lineIsHorizontal = true;
    }
    this.updateProperties({
      id: this.getId(),
      name: this.metaLength,
      value: this.units.fromPx('sizeUnit', lineLength),
      oldValue: this.baseDef[this.metaLength]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaIsHorizontal,
      value: lineIsHorizontal,
      oldValue: this.baseDef[this.metaIsHorizontal]
    });
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    if (this.baseDef.isHorizontal) {
      this.updateProperties({
        id: this.getId(),
        name: this.metaLength,
        value: this.units.fromPx('sizeUnit', metadata.widthPx),
        oldValue: this.baseDef[this.metaLength]
      });
    } else {
      this.updateProperties({
        id: this.getId(),
        name: this.metaLength,
        value: this.units.fromPx('sizeUnit', metadata.heightPx),
        oldValue: this.baseDef[this.metaLength]
      });
    }
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }
  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.getWidthNum());
  }
  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.getHeightNum());
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplateLine {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      // {
      //   name: this.metaId,parentName: null, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'Text',
      //   selectItems: [],
      //   isRequired: false,
      //   isEdited: false,
      // },
      {
        name: this.metaTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      // {
      //   name: this.metaWidth,parentName: null, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'ReadOnly',
      //   // widthの値は、isHorizontalがtrueだった場合にlengthと同じ値になる（システムが設定する）ので、ReadOnlyとする
      //   selectItems: [],
      //   isRequired: true, // ReadOnlyだからisRequired=falseでも良いが、システムが必ず設定しているはず、という意味も込めて必須。
      //   isEdited: false,
      // },
      // {
      //   name: this.metaHeight,parentName: null, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'ReadOnly',
      //   // heightの値は、isHorizontalがfalseだった場合にlengthと同じ値になる（システムが設定する）ので、ReadOnlyとする
      //   selectItems: [],
      //   isRequired: true, // ReadOnlyだからisRequired=falseでも良いが、システムが必ず設定しているはず、という意味も込めて必須。
      //   isEdited: false,
      // },
      {
        name: this.metaZIndex, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaBorderStyle, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'none', value: 'none' },
          { label: 'hidden', value: 'hidden' },
          { label: 'solid', value: 'solid' },
          { label: 'double', value: 'double' },
          { label: 'groove', value: 'groove' },
          { label: 'ridge', value: 'ridge' },
          { label: 'inset', value: 'inset' },
          { label: 'outset', value: 'outset' },
          { label: 'dashed', value: 'dashed' },
          { label: 'dotted', value: 'dotted' },
        ],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaBorderColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaLength, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaIsHorizontal, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        // たぶん、この分岐には入ってこないと思う（ReadOnlyなので直接編集できないため）
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.getWidth();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        // たぶん、この分岐には入ってこないと思う（ReadOnlyなので直接編集できないため）
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.getHeight();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.name] = metadata.value as number;
        this.zIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderTopStyle = this.getBorderTopStyle();
        this.borderLeftStyle = this.getBorderLeftStyle();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderTopColor = this.getBorderTopColor();
        this.borderLeftColor = this.getBorderLeftColor();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLength:
        this.baseDef[metadata.name] = metadata.value as number;
        // this.baseDef[this.metaWidth] = this.getWidthNum();
        // this.baseDef[this.metaHeight] = this.getHeightNum();
        this.width = this.getWidth();
        this.height = this.getHeight();
        this.setPreviewValue(metadata.name, metadata.value);
        this.setPreviewValue(this.metaWidth, this.getWidthNum());
        this.setPreviewValue(this.metaHeight, this.getHeightNum());
        break;
      case this.metaIsHorizontal:
        this.baseDef[metadata.name] = metadata.value as boolean;
        // this.baseDef[this.metaWidth] = this.getWidthNum();
        // this.baseDef[this.metaHeight] = this.getHeightNum();
        this.width = this.getWidth();
        this.height = this.getHeight();
        this.borderTopStyle = this.getBorderTopStyle();
        this.borderLeftStyle = this.getBorderLeftStyle();
        this.borderTopColor = this.getBorderTopColor();
        this.borderLeftColor = this.getBorderLeftColor();
        this.setPreviewValue(metadata.name, metadata.value);
        this.setPreviewValue(this.metaWidth, this.getWidthNum());
        this.setPreviewValue(this.metaHeight, this.getHeightNum());
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        // サイズの単位が変更された時、自身が持つサイズの値を再計算する
        // 自身が持つサイズのプロパティに新しい値を設定する＝未保存状態にする必要があるため
        // updateProperties()経由で新しい値を設定する
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLength,
          value: this.convertUnit(this.baseDef[this.metaLength], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLength]
        });
      })
    );
  }

  private getWidthNum(): number {
    let rtn: number;
    if (this.baseDef.isHorizontal) {
      rtn = this.baseDef.length;
    } else {
      rtn = 0;
    }
    return rtn;
  }
  private getHeightNum(): number {
    let rtn: number;
    if (this.baseDef.isHorizontal) {
      rtn = 0;
    } else {
      rtn = this.baseDef.length;
    }
    return rtn;
  }
  private getWidth(): string {
    return this.units.concatenate('sizeUnit', this.getWidthNum());
  }
  private getHeight(): string {
    return this.units.concatenate('sizeUnit', this.getHeightNum());
  }
  private getBorderTopStyle(): string {
    let rtn: string;
    if (this.baseDef.isHorizontal) {
      rtn = this.baseDef.borderStyle;
    } else {
      rtn = '';
    }
    return rtn;
  }
  private getBorderTopColor(): string {
    let rtn: string;
    if (this.baseDef.isHorizontal) {
      rtn = this.baseDef.borderColor;
    } else {
      rtn = '';
    }
    return rtn;
  }
  private getBorderLeftStyle(): string {
    let rtn: string;
    if (this.baseDef.isHorizontal) {
      rtn = '';
    } else {
      rtn = this.baseDef.borderStyle;
    }
    return rtn;
  }
  private getBorderLeftColor(): string {
    let rtn: string;
    if (this.baseDef.isHorizontal) {
      rtn = '';
    } else {
      rtn = this.baseDef.borderColor;
    }
    return rtn;
  }
}

/** *****************************************************************/
export class ReportImage extends ReportElement {
  // Angularオートバインディングプロパティ
  public top: string;
  public left: string;
  public width: string;
  public height: string;
  public zIndex: number;
  public src: string;

  // 関連インスタンス
  private units: ReportUnits;
  private fonts: ReportFonts;

  // テンプレートJSONに関するプロパティ
  private baseDef: TemplateImage;
  private readonly metaId = 'id';
  private readonly metaTop = 'y'; // JSONのプロパティ名とCSSのプロパティ名に差異があるのであえてこの変数名にしている
  private readonly metaLeft = 'x'; // JSONのプロパティ名とCSSのプロパティ名に差異があるのであえてこの変数名にしている
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaSrc = 'src';
  private readonly metaType = 'type'; // なんで用意されているんだろう。。。使い道が分からない。。。

  /**
   * コンストラクタ
   * @param index ReportImageの基となるTemplateJSON側のLine定義配列のインデックス値
   * @param units ReportImageが持つプロパティにサイズ単位を与えるために必要
   * @param templateImage TemplateJSON側の定義情報。省略可能
   */
  constructor(index: number, units: ReportUnits, fonts: ReportFonts, templateImage?: TemplateImage) {

    super(ElementType.Image, index, !(templateImage == null), templateImage ? templateImage.id : null);

    this.units = units;
    this.fonts = fonts;

    if (templateImage) {
      this.baseDef = templateImage;

    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0, // 初期値を0で仮設定
        y: 0, // 初期値を0で仮設定
        width: 0, // 初期値を0で仮設定
        height: 0, // 初期値を0で仮設定
        zIndex: 0, // 初期値を0で仮設定
        src: null,
        type: 'static', // 初期値をstaticで仮設定
        dynamicDataField: '',
        dynamicId: null
      };
    }

    this.zIndex = this.baseDef.zIndex;
    this.src = this.baseDef.src;

    this.top = this.units.concatenate('sizeUnit', this.baseDef.y);
    this.left = this.units.concatenate('sizeUnit', this.baseDef.x);
    this.width = this.units.concatenate('sizeUnit', this.baseDef.width);
    this.height = this.getHeight();

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }
  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaWidth]);
  }
  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaHeight]);
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplateImage {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      // {
      //   name: this.metaId,parentName: null, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'Text',
      //   selectItems: [],
      //   isRequired: false,
      //   isEdited: false,
      // },
      {
        name: this.metaTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaHeight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaZIndex, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaType, parentName: null, value: '',
        // このプロパティの使い道が良く分からない。。。
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'static', value: 'static' },
          { label: 'dynamic', value: 'dynamic' },
        ],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaSrc, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.getHeight();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.name] = metadata.value as number;
        this.zIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaSrc:
        this.baseDef[metadata.name] = metadata.value as string;
        this.src = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaType:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        // サイズの単位が変更された時、自身が持つサイズの値を再計算する
        // 自身が持つサイズのプロパティに新しい値を設定する＝未保存状態にする必要があるため
        // updateProperties()経由で新しい値を設定する
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.convertUnit(this.baseDef[this.metaWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaWidth]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaHeight,
          value: this.convertUnit(this.baseDef[this.metaHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaHeight]
        });
      })
    );

    // TemplateImage.titleプロパティには、なぜかFontFamilyがいないので、
    // ReportFonts.onUpdateFontFamily発火時に動かす処理は登録しない
  }

  private getHeight(): string {
    let rtn: string;
    if (
      this.baseDef.height !== null &&
      this.baseDef.height !== undefined &&
      this.baseDef.height > 0
    ) {
      rtn = this.units.concatenate('sizeUnit', this.baseDef.height);
    } else {
      rtn = 'auto';
    }
    return rtn;
  }
}

/** *****************************************************************/
export class ReportRectangle extends ReportElement {

  // Angularオートバインディングプロパティ
  public top: string;
  public left: string;
  public width: string;
  public height: string;
  public zIndex: number;
  public borderStyle: string;
  public borderWidth: string;
  public borderColor: string;
  public backgroundColor: string;

  // 関連インスタンス
  private units: ReportUnits;

  // テンプレートJSONに関するプロパティ
  private baseDef: TemplateRectangle;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaBorderStyle = 'borderStyle';
  private readonly metaBorderWidth = 'borderWidth';
  private readonly metaBorderColor = 'borderColor';
  private readonly metaBGColor = 'backgroundColor';

  /**
   * コンストラクタ
   * @param index ReportRectangleの基となるTemplateJSON側のLine定義配列のインデックス値
   * @param units ReportRectangleが持つプロパティにサイズ単位を与えるために必要
   * @param templateRect TemplateJSON側の定義情報。省略可能
   */
  constructor(index: number, units: ReportUnits, templateRect?: TemplateRectangle) {

    super(ElementType.Rectangle, index, !(templateRect == null), templateRect ? templateRect.id : null);

    this.units = units;

    if (templateRect) {
      this.baseDef = templateRect;

    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0, // 初期値を0で仮設定
        y: 0, // 初期値を0で仮設定
        width: 0, // 初期値を0で仮設定
        height: 0, // 初期値を0で仮設定
        zIndex: 0, // 初期値を0で仮設定
        borderStyle: '', // 初期値を空で仮設定
        borderWidth: '', // 初期値を空で仮設定
        borderColor: '', // 初期値を空で仮設定
        backgroundColor: '' // 初期値を空で仮設定
      };
    }

    this.top = this.units.concatenate('sizeUnit', this.baseDef.y);
    this.left = this.units.concatenate('sizeUnit', this.baseDef.x);
    this.width = this.units.concatenate('sizeUnit', this.baseDef.width);
    this.height = this.units.concatenate('sizeUnit', this.baseDef.height);
    this.zIndex = this.baseDef.zIndex;
    this.borderStyle = this.baseDef.borderStyle;
    this.borderColor = this.baseDef.borderColor;
    this.backgroundColor = this.baseDef.backgroundColor;
    const borderWidthNum = Number(this.baseDef.borderWidth);
    this.borderWidth = isNaN(borderWidthNum)
      ? this.baseDef.borderWidth
      : this.units.concatenate('sizeUnit', borderWidthNum);

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }
  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaWidth]);
  }
  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaHeight]);
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplateRectangle {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      // {
      //   name: this.metaId,parentName: null, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'Text',
      //   selectItems: [],
      //   isRequired: false,
      //   isEdited: false,
      // },
      {
        name: this.metaTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaHeight, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaZIndex, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      },
      {
        name: this.metaBorderStyle, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'none', value: 'none' },
          { label: 'hidden', value: 'hidden' },
          { label: 'solid', value: 'solid' },
          { label: 'double', value: 'double' },
          { label: 'groove', value: 'groove' },
          { label: 'ridge', value: 'ridge' },
          { label: 'inset', value: 'inset' },
          { label: 'outset', value: 'outset' },
          { label: 'dashed', value: 'dashed' },
          { label: 'dotted', value: 'dotted' },
        ],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaBorderWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaBorderColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
      {
        name: this.metaBGColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
      },
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.name] = metadata.value as number;
        this.zIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderStyle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderWidth:
        const borderWidthNum = Number(metadata.value as string);
        this.borderWidth = isNaN(borderWidthNum)
          ? metadata.value as string
          : this.units.concatenate('sizeUnit', borderWidthNum);
        this.baseDef[metadata.name] = this.borderWidth;
        this.setPreviewValue(metadata.name, this.borderWidth);
        break;
      case this.metaBorderColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBGColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.backgroundColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        // サイズの単位が変更された時、自身が持つサイズの値を再計算する
        // 自身が持つサイズのプロパティに新しい値を設定する＝未保存状態にする必要があるため
        // updateProperties()経由で新しい値を設定する
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.convertUnit(this.baseDef[this.metaWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaWidth]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaHeight,
          value: this.convertUnit(this.baseDef[this.metaHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaHeight]
        });
      })
    );
  }

}

/** *****************************************************************/
export class ReportSummary extends ReportElement {
  public defaultPageBreakRowCount: number;
  public hasInspectionSummaryLink: boolean;

  // Angularオートバインディングプロパティ
  public tableTop: string;
  public tableLeft: string;
  public tableWidth: string;
  public tableHeight: string;
  public tableRowHeight: string;
  public tableZIndex: number;

  public tableHeaderHeight: string;
  public tableHeaderBackgroundColor: string;
  public tableHeaderFontSize: string;
  public tableHeaderFontFamily: string;
  public tableHeaderHorizontalAlign: string;
  public tableIsHorizontal: boolean;
  public tableBorderWidth: string;
  public tableBorderStyle: string;
  public tableBorderColor: string;
  public tableHasBorderTop: boolean;
  public tableHasBorderBottom: boolean;
  public tableHasBorderLeft: boolean;
  public tableHasBorderRight: boolean;
  public tableHasRowBackground: boolean;
  public tableRowBackgroundColor: string;
  public tableColumns: TableColumn[];
  public tableMode: boolean;
  public dataMode: number;

  // public title: TemplateText;
  public titleTop: string;
  public titleLeft: string;
  public titleWidth: string;
  public titleHeight: string;
  public titlePadding: string;
  public titlePaddingLeft: string;
  public titlePaddingBottom: string;
  public titlePaddingRight: string;
  public titlePaddingTop: string;
  public titleZIndex: number;
  public titleText: string;
  public titleFontSize: string;
  public titleFontFamily: string;
  public titleFontColor: string;
  public titleFontStyle: string;
  public titleFontWeight: string;
  public titleLineHeight: string;
  public titleTextAlign: string;
  public titleVerticalAlign: string;
  public titleBorderWidth: string;
  public titleBorderStyle: string;
  public titleBorderColor: string;
  public titleBackgroundColor: string;
  public titleIsHorizontal: boolean;
  public titleJustifyContent: string;
  public titleAlignItems: string;

  public titleType: string;
  public titleFormat: string;
  public titleDynamicId: string;
  public titleInputType: string;
  public titleEditableId: string;
  public titleValidation: Validation;
  public titleRadioType: string;
  public titleRadioLabel: string;
  public titleOptions: Options[];
  public titleLabel: string;
  public titleValue: string;
  public titleInputGroup: string;

  public subTotal: SubTotal;
  public subTotalTitle: TemplateText;

  // 関連インスタンス
  private units: ReportUnits;
  private paperSize: ReportPaperSize;
  private pageMargin: ReportPageMargin;
  private header: ReportCommonSection;
  private footer: ReportCommonSection;

  // テンプレートJSONに関するプロパティ
  private baseDef: TemplateSummary;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';

  private readonly metaTable = 'table';

  /**
   * コンストラクタ
   * @param index ReportSummaryの基となるTemplateJSON側のLine定義配列のインデックス値
   * @param units ReportSummaryが持つプロパティにサイズ単位を与えるために必要
   * @param header ReportSummaryの高さを求めるときに必要
   * @param footer ReportSummaryの高さを求めるときに必要
   * @param paperSize ReportSummaryが持つプロパティにサイズ単位を与えるために必要自身の高さ、幅を求めるときに必要
   * @param pageMargin ReportSummary自身の余白を求めるときに必要
   * @param templateSummary TemplateJSON側の定義情報。省略可能
   */
  constructor(
    type: ElementType, index: number, units: ReportUnits, header: ReportCommonSection, footer: ReportCommonSection,
    paperSize: ReportPaperSize, pageMargin: ReportPageMargin, templateSummary?: TemplateSummary
  ) {

    super(type, index, !(templateSummary == null), templateSummary ? templateSummary.id : null);

    this.units = units;
    this.header = header;
    this.footer = footer;
    this.paperSize = paperSize;
    this.pageMargin = pageMargin;

    if (templateSummary) {
      this.baseDef = templateSummary;

    } else {
      const title = {
        id: this.generateUUID(),
        x: null,
        y: null,
        width: null,
        height: null,
        zIndex: null,
        borderStyle: null,
        borderWidth: null,
        borderColor: null,
        backgroundColor: null,
        type: 'static', // 初期値をstaticで仮設定
        text: null,
        format: null,
        fontSize: null,
        fontFamily: null,
        fontColor: null,
        fontStyle: null,
        fontWeight: null,
        horizontalAlign: 'left', // 初期値をleftで仮設定
        verticalAlign: 'center', // 初期値をcenterで仮設定
        isHorizontal: true,
        lineHeight: null,
        dynamicDataField: null, // 初期値をnullで仮設定
        dynamicId: null, // 初期値をnullで仮設定
        inputType: 'text', // 初期値をtextで仮設定
        editableId: null,
        validation: null,
        radioType: null,
        radioLabel: null,
        options: null,
        label: null,
        value: null,
        inputGroup: null,
        padding: null,
        paddingTop: 0.5,
        paddingLeft: 0.5,
        paddingBottom: 0.5,
        paddingRight: 0.5,
        multiSelectDelimiter: 'comma',
        isTableText: false
      };

      const table = {
        id: this.generateUUID(),
        x: 0, // 初期値を0で仮設定
        y: 0, // 初期値を0で仮設定
        width: 0, // 初期値を0で仮設定
        height: 0, // 初期値を0で仮設定
        zIndex: 0, // 初期値を0で仮設定
        headerHeight: 0, // 初期値を0で仮設定
        headerBackgroundColor: null,
        headerFontSize: 0, // 初期値を0で仮設定
        headerFontFamily: null,
        horizontalAlign: null,
        borderColor: null,
        borderStyle: null,
        borderWidth: null,
        hasBorderTop: null,
        hasBorderBottom: null,
        hasBorderLeft: null,
        hasBorderRight: null,
        hasRowBackground: null,
        rowBackgroundColor: null,
        columns: Array<TableColumn>(),
        tableMode: false,
        dataMode: 0
      };

      this.baseDef = {
        id: this.generateUUID(),
        x: 0, // 初期値を0で仮設定
        y: 0, // 初期値を0で仮設定
        width: 0, // 初期値を0で仮設定
        height: 0, // 初期値を0で仮設定
        zIndex: 0, // 初期値を0で仮設定
        defaultPageBreakRowCount: 0,
        hasInspectionSummaryLink: true,
        title: title,
        table: table,
        subTotal: {
          title: title,
          table: table
        },
        isEmptyFolderDisplayed: false
      };
    }
    this.defaultPageBreakRowCount = this.baseDef.defaultPageBreakRowCount;
    this.hasInspectionSummaryLink = this.baseDef.hasInspectionSummaryLink;
    // title
    this.titleTop = this.units.concatenate('sizeUnit', this.baseDef.title.y);
    this.titleLeft = this.units.concatenate('sizeUnit', this.baseDef.title.x);
    this.titleWidth = this.units.concatenate('sizeUnit', this.baseDef.title.width);
    this.titleHeight = this.units.concatenate('sizeUnit', this.baseDef.title.height);
    this.titleZIndex = this.baseDef.title.zIndex;
    this.titleFontColor = this.baseDef.title.fontColor;
    this.titleFontFamily = this.baseDef.title.fontFamily;
    this.titleFontSize = this.units.concatenate('fontSizeUnit', this.baseDef.title.fontSize);
    this.titleFontStyle = this.baseDef.title.fontStyle;
    this.titleFontWeight = this.baseDef.title.fontWeight;
    this.titleVerticalAlign = this.baseDef.title.verticalAlign;
    this.titleTextAlign = this.baseDef.title.horizontalAlign;
    this.titleLineHeight = this.units.concatenate('sizeUnit', this.baseDef.title.lineHeight);
    this.titleBackgroundColor = this.baseDef.title.backgroundColor;
    this.titleBorderColor = this.baseDef.title.borderColor;
    this.titleBorderStyle = this.baseDef.title.borderStyle;
    this.titleBorderWidth = this.units.concatenate('sizeUnit', this.baseDef.title.borderWidth);
    this.titleText = this.getText();
    this.titleJustifyContent = this.getJustifyContent();
    this.titleAlignItems = this.getAlignItems();
    this.titleType = this.baseDef.title.type;
    this.titleFormat = this.baseDef.title.format;
    this.titleDynamicId = this.baseDef.title.dynamicId;
    this.titleInputType = this.baseDef.title.inputType;
    this.titleEditableId = this.baseDef.title.editableId;
    this.titleValidation = this.baseDef.title.validation;
    this.titleRadioType = this.baseDef.title.radioType;
    this.titleRadioLabel = this.baseDef.title.radioLabel;
    this.titleOptions = this.baseDef.title.options;
    this.titleLabel = this.baseDef.title.label;
    this.titleValue = this.baseDef.title.value;
    this.titleInputGroup = this.baseDef.title.inputGroup;

    // table
    this.tableTop = this.units.concatenate('sizeUnit', this.baseDef.table.y);
    this.tableLeft = this.units.concatenate('sizeUnit', this.baseDef.table.x);
    this.tableWidth = this.units.concatenate('sizeUnit', this.baseDef.table.width);
    if (this.baseDef.table.height === 0) {
      this.tableHeight = this.units.concatenate('sizeUnit', this.getHeightNum('table'));
      this.tableRowHeight = this.units.concatenate('sizeUnit', this.getHeightNum('table') / this.baseDef.defaultPageBreakRowCount);
    } else {
      this.tableHeight = this.units.concatenate('sizeUnit', this.baseDef.table.height);
      this.tableRowHeight = this.units.concatenate('sizeUnit', this.baseDef.table.height / this.baseDef.defaultPageBreakRowCount);
    }
    this.tableZIndex = this.baseDef.table.zIndex;
    this.tableHeaderHeight = this.units.concatenate('sizeUnit', this.baseDef.table.headerHeight);
    this.tableHeaderFontSize = this.units.concatenate('fontSizeUnit', this.baseDef.table.headerFontSize);
    this.tableHeaderFontFamily = this.baseDef.table.headerFontFamily;
    this.tableHeaderHorizontalAlign = this.baseDef.table.horizontalAlign;
    this.tableHeaderBackgroundColor = this.baseDef.table.headerBackgroundColor;
    this.tableRowBackgroundColor = this.baseDef.table.rowBackgroundColor;
    this.tableBorderStyle = this.baseDef.table.borderStyle;
    this.tableBorderWidth = this.units.concatenate('sizeUnit', this.baseDef.table.borderWidth);
    this.tableBorderColor = this.baseDef.table.borderColor;
    this.tableHasBorderTop = this.baseDef.table.hasBorderTop;
    this.tableHasBorderBottom = this.baseDef.table.hasBorderBottom;
    this.tableHasBorderLeft = this.baseDef.table.hasBorderLeft;
    this.tableHasBorderRight = this.baseDef.table.hasBorderRight;
    this.tableHasRowBackground = this.baseDef.table.hasRowBackground;
    this.tableColumns = this.baseDef.table.columns;
    this.tableMode = this.baseDef.table.tableMode;
    this.dataMode = this.baseDef.table.dataMode;
    // subtotal
    // this.subTotal = this.baseDef[this.metaTable][this.metaSubTotal];
    this.subTotal = this.baseDef.subTotal;

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTable][this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaTable][this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaTable][this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaTable][this.metaHeight]
    });
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTable][this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaTable][this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTable][this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaTable][this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaTable][this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaTable][this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTable][this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTable][this.metaTop]);
  }
  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTable][this.metaWidth]);
  }
  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTable][this.metaHeight]);
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplateSummary {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      // {
      //   name: this.metaId, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'Text',
      //   selectItems: [],
      //   isRequired: false,
      //   isEdited: false,
      // },
      // {
      //   name: this.metaTable, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'JSON',
      //   selectItems: [],
      //   isRequired: true,
      //   isEdited: false,
      // }
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[this.metaTable][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[this.metaTable][metadata.name] = metadata.value as number;
        this.tableTop = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[this.metaTable][metadata.name] = metadata.value as number;
        this.tableLeft = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[this.metaTable][metadata.name] = metadata.value as number;
        this.tableWidth = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[this.metaTable][metadata.name] = metadata.value as number;
        this.tableHeight = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[this.metaTable][metadata.name] = metadata.value as number;
        this.tableZIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTable:
        this.baseDef[metadata.name] = JSON.parse(metadata.value as string);
        this.tableTop = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaTop] as number);
        this.tableLeft = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaLeft] as number);
        this.tableWidth = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaWidth] as number);
        // this.tableHeight = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaHeight] as number);
        this.tableZIndex = this.baseDef[metadata.name][this.metaZIndex] as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.pageMargin.onUpdateLeft.subscribe((metadata: UpdateMetadata) => {
        // paddingLeftの再設定
        this.tableWidth = this.getWidth('table');
        // tableWidthの再設定
        // →tableWidthプロパティの値が変わる＝要Saveな状態にする必要があるので、
        //  updateProperties()経由でプロパティを設定
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.getWidthNum('table'),
          oldValue: this.baseDef.table.width
        });
      })
    );
    subscribers.push(
      this.pageMargin.onUpdateRight.subscribe((metadata: UpdateMetadata) => {
        // paddingRightの再設定
        this.tableWidth = this.getWidth('table');
        // tableWidthの再設定
        // →tableWidthプロパティの値が変わる＝要Saveな状態にする必要があるので、
        //  updateProperties()経由でプロパティを設定
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.getWidthNum('table'),
          oldValue: this.baseDef.table.width
        });
      })
    );
    subscribers.push(
      this.paperSize.onUpdatePaperSize.subscribe((metadata: UpdateMetadata) => {
        // tableWidth、tableHeightの再設定
        this.tableWidth = this.getWidth('table');
        this.tableHeight = this.getHeight('table');
        // tableWidthの再設定
        // →tableWidthプロパティの値が変わる＝要Saveな状態にする必要があるので、
        //  updateProperties()経由でプロパティを設定
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.getWidthNum('table'),
          oldValue: this.baseDef.table.width
        });
      })
    );
    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        // サイズの単位が変更された時、自身が持つサイズの値を再計算する
        // 自身が持つサイズのプロパティに新しい値を設定する＝未保存状態にする必要があるため
        // updateProperties()経由で新しい値を設定する
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTable][this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTable][this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaTable][this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTable][this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.convertUnit(this.baseDef[this.metaTable][this.metaWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTable][this.metaWidth]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaHeight,
          value: this.convertUnit(this.baseDef[this.metaTable][this.metaHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTable][this.metaHeight]
        });
      })
    );
  }
  private getHeightNum(parentName: string): number {
    let rtn = this.paperSize.getHeightNum();
    rtn = this.pageMargin.getPaddedHeightNum(rtn);
    if (parentName === 'table') {
      rtn = rtn - this.header.getHeightNum()
        - this.footer.getHeightNum()
        - (this.baseDef.title.height
            + this.baseDef.table.headerHeight
            + this.baseDef.table.y * 2);
    }
    rtn = Math.floor(rtn);
    return rtn;
  }
  private getHeight(parentName: string): string {
    return [this.getHeightNum(parentName), 'mm'].join('');
  }
  private getWidthNum(parentName: string): number {
    let rtn = this.paperSize.getWidthNum();
    let x: number;
    if (parentName === 'title') {
      x = this.baseDef.title.x;
    } else if (parentName === 'table') {
      x = this.baseDef.table.x;
    }
    if (this.type === ElementType.Summary) {
      // 検査サマリの横幅を設定
      rtn = this.pageMargin.getPaddedWidthNum(rtn) - (x * 2);
    } else {
      // 検査画像の横幅を設定（既存のJsonの値を変えないように調整）
      rtn = this.pageMargin.getPaddedWidthNum(rtn) - (x * 2) - 5;
    }
    rtn = Math.floor(rtn);
    return rtn;
  }
  private getWidth(parentName: string): string {
    return [this.getWidthNum(parentName), 'mm'].join('');
  }
  public getThWidth(column: TableColumn): string {
    return [this.getWidthNum('table') * (column.widthRatio / 100), 'mm'].join('');
  }

  private getText(): string {
    let rtnText = '';
    switch (this.baseDef.title.type) {
      case 'static':
        rtnText = this.baseDef.title.text;
        break;
      case 'dynamic':
        rtnText = this.baseDef.title.dynamicDataField ? '[' + this.baseDef.title.dynamicDataField + ']' : '[Dynamic Text]';
        break;
      case 'dynamic-editable':
        rtnText = this.baseDef.title.editableId ? '{' + this.baseDef.title.editableId + '}' : '{Editable Text}';
        break;
    }
    return rtnText;
  }

  private getJustifyContent(): string {
    let rtn: string;
    switch (this.baseDef.title.horizontalAlign) {
      case 'left':
        rtn = 'flex-start';
        break;
      case 'center':
        rtn = 'center';
        break;
      case 'right':
        rtn = 'flex-end';
        break;
    }
    return rtn;
  }

  private getAlignItems(): string {
    let rtn: string;
    switch (this.baseDef.title.verticalAlign) {
      case 'top':
        rtn = 'flex-start';
        break;
      case 'middle':
        rtn = 'center';
        break;
      case 'bottom':
        rtn = 'flex-end';
        break;
    }
    return rtn;
  }

  public arrayNumberLength(): any[] {
    return [...Array(this.defaultPageBreakRowCount)];
  }

  public getTitleTopNum(): number {
    return this.baseDef.title.y;
  }
}

/** *****************************************************************/
export class ReportDataPlate extends ReportElement {

  // Angularオートバインディングプロパティ
  public tableTop: string;
  public tableLeft: string;
  public tableWidth: string;
  public tableHeight: string;
  public tableZIndex: number;

  // 関連インスタンス
  private units: ReportUnits;
  private paperSize: ReportPaperSize;
  private pageMargin: ReportPageMargin;
  private header: ReportCommonSection;
  private footer: ReportCommonSection;

  // テンプレートJSONに関するプロパティ
  private baseDef: TemplateDataPlate;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';

  private readonly metaImageInfo = 'imageInfo';

  /**
   * コンストラクタ
   * @param index ReportDataPlateの基となるTemplateJSON側のLine定義配列のインデックス値
   * @param units ReportDataPlateが持つプロパティにサイズ単位を与えるために必要
   * @param header ReportDataPlateの高さを求めるときに必要
   * @param footer ReportDataPlateの高さを求めるときに必要
   * @param paperSize ReportDataPlateが持つプロパティにサイズ単位を与えるために必要自身の高さ、幅を求めるときに必要
   * @param pageMargin ReportDataPlate自身の余白を求めるときに必要
   * @param templateDataPlate TemplateJSON側の定義情報。省略可能
   */
  constructor(
    type: ElementType, index: number, units: ReportUnits, header: ReportCommonSection, footer: ReportCommonSection,
    paperSize: ReportPaperSize, pageMargin: ReportPageMargin, templateDataPlate?: TemplateDataPlate
  ) {

    super(type, index, !(templateDataPlate == null), templateDataPlate ? templateDataPlate.id : null);

    this.units = units;
    this.header = header;
    this.footer = footer;
    this.paperSize = paperSize;
    this.pageMargin = pageMargin;

    if (templateDataPlate) {
      this.baseDef = templateDataPlate;

    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0, // 初期値を0で仮設定
        y: 0, // 初期値を0で仮設定
        width: 0, // 初期値を0で仮設定
        height: 0, // 初期値を0で仮設定
        zIndex: 0, // 初期値を0で仮設定
        title: null, // 初期値をnullで仮設定
        imageInfo: null, // 初期値をnullで仮設定
      };
    }
    // this.baseDef.width = this.getWidthNum();

    this.tableTop = this.units.concatenate('sizeUnit', this.baseDef[this.metaImageInfo][this.metaTop]);
    this.tableLeft = this.units.concatenate('sizeUnit', this.baseDef[this.metaImageInfo][this.metaLeft]);
    this.tableWidth = this.units.concatenate('sizeUnit', this.baseDef[this.metaImageInfo][this.metaWidth]);
    this.tableHeight = this.units.concatenate('sizeUnit', this.baseDef[this.metaImageInfo][this.metaHeight]);

    // this.tableWidth = this.getWidth();
    // this.tableHeight = this.getHeight();

    this.tableZIndex = this.baseDef[this.metaImageInfo][this.metaZIndex];
    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaHeight]
    });
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaImageInfo][this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaImageInfo][this.metaTop]);
  }
  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaImageInfo][this.metaWidth]);
  }
  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaImageInfo][this.metaHeight]);
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplateDataPlate {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      // {
      //   name: this.metaId, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'Text',
      //   selectItems: [],
      //   isRequired: false,
      //   isEdited: false,
      // },
      // {
      //   name: this.metaTable, value: '',
      //   isFieldType: (type: FieldType): boolean => type === 'JSON',
      //   selectItems: [],
      //   isRequired: true,
      //   isEdited: false,
      // }
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[this.metaImageInfo][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[this.metaImageInfo][metadata.name] = metadata.value as number;
        this.tableTop = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[this.metaImageInfo][metadata.name] = metadata.value as number;
        this.tableLeft = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[this.metaImageInfo][metadata.name] = metadata.value as number;
        this.tableWidth = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[this.metaImageInfo][metadata.name] = metadata.value as number;
        this.tableHeight = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[this.metaImageInfo][metadata.name] = metadata.value as number;
        this.tableZIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaImageInfo:
        this.baseDef[metadata.name] = JSON.parse(metadata.value as string);
        this.tableTop = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaTop] as number);
        this.tableLeft = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaLeft] as number);
        this.tableWidth = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaWidth] as number);
        // this.tableHeight = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaHeight] as number);
        this.tableZIndex = this.baseDef[metadata.name][this.metaZIndex] as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    // subscribers.push(
    //   this.paperSize.onUpdatePaperSize.subscribe((metadata: UpdateMetadata) => {
    //     // tableWidth、tableHeightの再設定
    //     this.tableWidth = this.getWidth();
    //     this.tableHeight = this.getHeight();
    //     // tableWidthの再設定
    //     // →tableWidthプロパティの値が変わる＝要Saveな状態にする必要があるので、
    //     //  updateProperties()経由でプロパティを設定
    //     this.updateProperties({
    //       id: this.getId(),
    //       name: this.metaWidth,
    //       value: this.getWidthNum(),
    //       oldValue: this.baseDef.width
    //     });
    //   })
    // );
    // subscribers.push(
    //   this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
    //     // サイズの単位が変更された時、自身が持つサイズの値を再計算する
    //     // 自身が持つサイズのプロパティに新しい値を設定する＝未保存状態にする必要があるため
    //     // updateProperties()経由で新しい値を設定する
    //     this.updateProperties({
    //       id: this.getId(),
    //       name: this.metaTop,
    //       value: this.convertUnit(this.baseDef[this.metaImageInfo][this.metaTop],
    //          metadata.oldValue as string, metadata.value as string),
    //       oldValue: this.baseDef[this.metaImageInfo][this.metaTop]
    //     });
    //     this.updateProperties({
    //       id: this.getId(),
    //       name: this.metaLeft,
    //       value: this.convertUnit(this.baseDef[this.metaImageInfo][this.metaLeft],
    //          metadata.oldValue as string, metadata.value as string),
    //       oldValue: this.baseDef[this.metaImageInfo][this.metaLeft]
    //     });
    //     this.updateProperties({
    //       id: this.getId(),
    //       name: this.metaWidth,
    //       value: this.convertUnit(this.baseDef[this.metaImageInfo][this.metaWidth],
    //          metadata.oldValue as string, metadata.value as string),
    //       oldValue: this.baseDef[this.metaImageInfo][this.metaWidth]
    //     });
    //     this.updateProperties({
    //       id: this.getId(),
    //       name: this.metaHeight,
    //       value: this.convertUnit(this.baseDef[this.metaImageInfo][this.metaHeight],
    //          metadata.oldValue as string, metadata.value as string),
    //       oldValue: this.baseDef[this.metaImageInfo][this.metaHeight]
    //     });
    //   })
    // );
  }
  private getHeightNum(): number {
    let rtn = this.paperSize.getHeightNum();
    rtn = this.pageMargin.getPaddedHeightNum(rtn);
    rtn = rtn - this.header.getHeightNum() - this.footer.getHeightNum() - (this.baseDef.y * 2);
    rtn = Math.floor(rtn);
    return rtn;
  }
  private getHeight(): string {
    return [this.getHeightNum(), 'mm'].join('');
  }
  private getWidthNum(): number {
    let rtn = this.paperSize.getWidthNum();
    if (this.type === ElementType.Summary) {
      // 検査サマリの横幅を設定
      rtn = this.pageMargin.getPaddedWidthNum(rtn) - (this.baseDef.x * 2);
    } else {
      // 検査画像の横幅を設定（既存のJsonの値を変えないように調整）
      rtn = this.pageMargin.getPaddedWidthNum(rtn) - (this.baseDef.x * 2) - 5;
    }
    rtn = Math.floor(rtn);
    return rtn;
  }
  private getWidth(): string {
    return [this.getWidthNum(), 'mm'].join('');
  }
}

export class ReportDataPlateOffline extends ReportElement {

  public top: string;
  public left: string;
  public width: string;
  public height: string;
  public zIndex: number;

  // public title: TemplateText;
  public titleTop: string;
  public titleLeft: string;
  public titleWidth: string;
  public titleHeight: string;
  public titlePadding: string;
  public titlePaddingLeft: string;
  public titlePaddingBottom: string;
  public titlePaddingRight: string;
  public titlePaddingTop: string;
  public titleZIndex: number;
  public titleText: string;
  public titleFontSize: string;
  public titleFontFamily: string;
  public titleFontColor: string;
  public titleFontStyle: string;
  public titleFontWeight: string;
  public titleLineHeight: string;
  public titleTextAlign: string;
  public titleVerticalAlign: string;
  public titleBorderWidth: string;
  public titleBorderStyle: string;
  public titleBorderColor: string;
  public titleBackgroundColor: string;
  public titleIsHorizontal: boolean;
  public titleJustifyContent: string;
  public titleAlignItems: string;

  public titleType: string;
  public titleFormat: string;
  public titleDynamicId: string;
  public titleInputType: string;
  public titleEditableId: string;
  public titleValidation: Validation;
  public titleRadioType: string;
  public titleRadioLabel: string;
  public titleOptions: Options[];
  public titleLabel: string;
  public titleValue: string;
  public titleInputGroup: string;

  public imageInfo: TemplateDataPlateImageInfo;
  public imageInfoTop: string;
  public imageInfoLeft: string;
  public imageInfoWidth: string;
  public imageInfoHeight: string;
  public imageInfoZIndex: number;
  public imageInfoTitle: TemplateDataPlateText;
  public imageInfoTitleTop: string;
  public imageInfoTitleLeft: string;
  public imageInfoTitleWidth: string;
  public imageInfoTitleHeight: string;
  public imageInfoTitleZIndex: number;
  public imageInfoTitleText: string;
  public imageInfoImage: TemplateImage;
  public imageInfoImageTop: string;
  public imageInfoImageLeft: string;
  public imageInfoImageWidth: string;
  public imageInfoImageHeight: string;
  public imageInfoImageZIndex: number;
  public imageInfoImageCaption: string;
  public imageInfoImageSrc: string;
  public imageInfoImageType: string;
  public imageInfoImageOrientation: number;

  private units: ReportUnits;
  private fonts: ReportFonts;
  private paperSize: ReportPaperSize;
  private pageMargin: ReportPageMargin;
  private header: ReportCommonSection;
  private footer: ReportCommonSection;

  private baseDef: TemplateDataPlate;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaTitle = 'title';
  private readonly metaBorderWidth = 'borderWidth';
  private readonly metaBorderStyle = 'borderStyle';
  private readonly metaBorderColor = 'borderColor';
  private readonly metaBGColor = 'backgroundColor';
  private readonly metaFontSize = 'fontSize';
  private readonly metaFontFamily = 'fontFamily';
  private readonly metaFontStyle = 'fontStyle';
  private readonly metaFontWeight = 'fontWeight';
  private readonly metaFontColor = 'fontColor';
  private readonly metaTextAlign = 'horizontalAlign';
  private readonly metaVerticalAlign = 'verticalAlign';
  private readonly metaLineHeight = 'lineHeight';
  private readonly metaPadding = 'padding';
  private readonly metaPaddingLeft = 'paddingLeft';
  private readonly metaPaddingBottom = 'paddingBottom';
  private readonly metaPaddingRight = 'paddingRight';
  private readonly metaPaddingTop = 'paddingTop';
  private readonly metaText = 'text';

  private readonly metaIsHorizontal = 'isHorizontal';
  private readonly metaType = 'type';
  private readonly metaDynamicId = 'dynamicId';
  private readonly metaEditableId = 'editableId';
  private readonly metaInputType = 'inputType';
  private readonly metaValidation = 'validation';
  private readonly metaFormat = 'format';
  private readonly metaRequired = 'required';
  private readonly metaMaxLength = 'maxLength';
  private readonly metaRadioType = 'radioType';
  private readonly metaRadioLabel = 'radioLabel';
  private readonly metaOptions = 'options';
  private readonly metaLabel = 'label';
  private readonly metaValue = 'value';
  private readonly metaInputGroup = 'inputGroup';

  private readonly metaImageInfo = 'imageInfo';
  private readonly metaImage = 'image';
  private readonly metaCaption = 'caption';
  private readonly metaSrc = 'src';
  private readonly metaOrientaion = 'orientation';

  constructor(
    type: ElementType,
    index: number,
    units: ReportUnits,
    fonts: ReportFonts,
    header: ReportCommonSection,
    footer: ReportCommonSection,
    paperSize: ReportPaperSize,
    pageMargin: ReportPageMargin,
    templateDataPlate?: TemplateDataPlate
  ) {
    super(type, index, !(templateDataPlate == null), templateDataPlate ? templateDataPlate.id : null);

    this.units = units;
    this.fonts = fonts;
    this.header = header;
    this.footer = footer;
    this.paperSize = paperSize;
    this.pageMargin = pageMargin;

    if (templateDataPlate) {
      this.baseDef = templateDataPlate;

    } else {
      const title = {
        id: this.generateUUID(),
        x: 0, // 初期値を0で仮設定
        y: 0, // 初期値を0で仮設定
        width: Math.floor(this.pageMargin.getPaddedWidthNum(this.paperSize.getWidthNum())), // 初期値を0で仮設定
        height: Math.floor(this.pageMargin.getPaddedHeightNum(this.paperSize.getHeightNum())), // 初期値を0で仮設定
        zIndex: 0, // 初期値を0で仮設定
        borderColor: null,
        borderStyle: null,
        borderWidth: null,
        backgroundColor: null,
        type: 'static', // 初期値をstaticで仮設定
        text: null,
        format: null,
        fontSize: null,
        fontFamily: null,
        fontColor: null,
        fontStyle: null,
        fontWeight: null,
        horizontalAlign: 'left', // 初期値をleftで仮設定
        verticalAlign: 'center', // 初期値をcenterで仮設定
        isHorizontal: true, // 初期値をtrueで設定
        lineHeight: null,
        dynamicId: null,
        dynamicDataField: null, // 初期値をnullで仮設定
        inputType: 'text', // 初期値をtextで仮設定
        editableId: null,
        validation: null,
        radioType: null,
        radioLabel: null,
        options: null,
        label: null,
        value: null,
        inputGroup: null,
        padding: null,
        paddingRight: 0.5,
        paddingBottom: 0.5,
        paddingLeft: 0.5,
        paddingTop: 0.5,
        multiSelectDelimiter: 'comma',
        isTableText: false
      };
      const imageInfoTitle = {
        id: this.generateUUID(),
        x: 0, // 初期値を0で仮設定
        y: 0, // 初期値を0で仮設定
        width: Math.floor(this.pageMargin.getPaddedWidthNum(this.paperSize.getWidthNum())), // 初期値を0で仮設定
        height: 0, // 初期値を0で仮設定
        zIndex: 0, // 初期値を0で仮設定
        borderColor: null,
        borderStyle: null,
        borderWidth: null,
        backgroundColor: null,
        type: 'static', // 初期値をstaticで仮設定
        text: null,
        format: null,
        fontSize: null,
        fontFamily: null,
        fontColor: null,
        fontStyle: null,
        fontWeight: null,
        horizontalAlign: 'left', // 初期値をleftで仮設定
        verticalAlign: 'center', // 初期値をcenterで仮設定
        isHorizontal: true, // 初期値をtrueで設定
        lineHeight: null,
        dynamicId: null,
        dynamicDataField: null, // 初期値をnullで仮設定
        inputType: 'text', // 初期値をtextで仮設定
        editableId: null,
        validation: null,
        radioType: null,
        radioLabel: null,
        options: null,
        label: null,
        value: null,
        inputGroup: null,
        padding: null,
        paddingRight: 0.5,
        paddingBottom: 0.5,
        paddingLeft: 0.5,
        paddingTop: 0.5,
        position: 'top', // 初期値をtopで仮設定
        multiSelectDelimiter: 'comma',
        isTableText: false
      };
      const imageInfoImage = {
        id: this.generateUUID(),
        x: 0, // 初期値を0で仮設定
        y: 0, // 初期値を0で仮設定
        width: Math.floor(this.pageMargin.getPaddedWidthNum(this.paperSize.getWidthNum()) / 2), // 初期値を0で仮設定
        height: 0, // 初期値を0で仮設定
        zIndex: 0, // 初期値を0で仮設定
        src: null,
        type: 'static', // 初期値をstaticで仮設定
        dynamicDataField: null, // 初期値をnullで仮設定
        dynamicId: null
      };
      this.baseDef = {
        id: this.generateUUID(),
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        zIndex: 0,
        title: title,
        imageInfo: {
          id: this.generateUUID(),
          x: 0,
          y: 0,
          width: 0,
          height: 0,
          title: imageInfoTitle,
          image: imageInfoImage
        }
      };
    }
    // title
    this.titleTop = this.units.concatenate('sizeUnit', this.baseDef.title.y);
    this.titleLeft = this.units.concatenate('sizeUnit', this.baseDef.title.x);
    this.titleWidth = this.units.concatenate('sizeUnit', this.baseDef.title.width);
    this.titleHeight = this.units.concatenate('sizeUnit', this.baseDef.title.height);
    this.titleZIndex = this.baseDef.title.zIndex;
    this.titleFontColor = this.baseDef.title.fontColor;
    this.titleFontFamily = this.baseDef.title.fontFamily;
    this.titleFontSize = this.units.concatenate('fontSizeUnit', this.baseDef.title.fontSize);
    this.titleFontStyle = this.baseDef.title.fontStyle;
    this.titleFontWeight = this.baseDef.title.fontWeight;
    this.titleVerticalAlign = this.baseDef.title.verticalAlign;
    this.titleTextAlign = this.baseDef.title.horizontalAlign;
    this.titleLineHeight = this.units.concatenate('sizeUnit', this.baseDef.title.lineHeight);
    this.titleBackgroundColor = this.baseDef.title.backgroundColor;
    this.titleBorderColor = this.baseDef.title.borderColor;
    this.titleBorderStyle = this.baseDef.title.borderStyle;
    this.titleBorderWidth = this.units.concatenate('sizeUnit', this.baseDef.title.borderWidth);
    this.titleText = this.getText(this.baseDef.title);
    this.titleJustifyContent = this.getJustifyContent();
    this.titleAlignItems = this.getAlignItems();
    this.titleType = this.baseDef.title.type;
    this.titleFormat = this.baseDef.title.format;
    this.titleDynamicId = this.baseDef.title.dynamicId;
    this.titleInputType = this.baseDef.title.inputType;
    this.titleEditableId = this.baseDef.title.editableId;
    this.titleValidation = this.baseDef.title.validation;
    this.titleRadioType = this.baseDef.title.radioType;
    this.titleRadioLabel = this.baseDef.title.radioLabel;
    this.titleOptions = this.baseDef.title.options;
    this.titleLabel = this.baseDef.title.label;
    this.titleValue = this.baseDef.title.value;
    this.titleInputGroup = this.baseDef.title.inputGroup;
    // imageInfo
    this.imageInfoTop = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.y);
    this.imageInfoLeft = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.x);
    this.imageInfoWidth = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.width);
    this.imageInfoHeight = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.height);
    // imageInfo - title
    this.imageInfoTitle = this.baseDef.imageInfo.title;
    if (this.imageInfoTitle !== null) {
      this.imageInfoTitleTop = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.title.y);
      this.imageInfoTitleLeft = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.title.x);
      this.imageInfoTitleWidth = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.title.width);
      this.imageInfoTitleHeight = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.title.height);
      this.imageInfoTitleZIndex = this.baseDef.imageInfo.title.zIndex;
      this.imageInfoTitleText = this.getText(this.baseDef.imageInfo.title);
    }
    // imageInfo - image
    this.imageInfoImage = this.baseDef.imageInfo.image;
    if (this.imageInfoImage !== null) {
      this.imageInfoImageTop = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.image.y);
      this.imageInfoImageLeft = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.image.x);
      this.imageInfoImageWidth = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.image.width);
      this.imageInfoImageHeight = this.units.concatenate('sizeUnit', this.baseDef.imageInfo.image.height);
      this.imageInfoImageZIndex = this.baseDef.imageInfo.image.zIndex;
    }

    this.setChainUpdate();
    this.setPreviewMetadata();
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTitle][this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaTitle][this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaTitle][this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaTitle][this.metaHeight]
    });
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaImageInfo][this.metaLeft]
    });
  }

  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTitle][this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaTitle][this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaTitle][this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaTitle][this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaImageInfo][this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaImageInfo][this.metaTop]);
  }
  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaImageInfo][this.metaWidth]);
  }
  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaImageInfo][this.metaHeight]);
  }

  public getTemplateJSON(): TemplateDataPlate {
    return this.baseDef;
  }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaLeft, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaTop, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaWidth, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaHeight, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaType, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'static', value: 'static' },
          { label: 'dynamic', value: 'dynamic' },
          { label: 'dynamic-editable', value: 'dynamic-editable' },
        ],
        isRequired: true,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaInputType, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'text', value: 'text' },
          { label: 'textArea', value: 'textArea' },
          { label: 'radio', value: 'radio' },
          { label: 'select', value: 'select' },
          { label: 'date', value: 'date' },
        ],
        isRequired: true,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaText, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'TextArea',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaDynamicId, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: DYNAMIC_SELECT_ITEM,
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaEditableId, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaFormat, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: '01/01/2019', value: 'MM/dd/yyyy' },
          { label: '1/1/2019', value: 'M/d/yyyy' },
          { label: '1/1/19', value: 'M/d/yy' },
          { label: '2019/1/1', value: 'yyyy/M/d' },
          { label: '19/1/1', value: 'yy/M/d' },
          { label: '2019-01-01', value: 'yyyy-MM-dd' },
          { label: '2019-1-1', value: 'yyyy-M-d' },
          { label: '19-01-01', value: 'yy-MM-dd' },
          { label: 'January 1, 2019', value: 'MMMM d, yyyy' },
          { label: 'January 01, 2019', value: 'MMMM dd, yyyy' },
          { label: 'Tuesday, January 01, 2019', value: 'dddd, MMMM dd, yyyy' },
          { label: 'Tuesday, Jan 1, 2019', value: 'dddd, MMM d, yyy' },
          { label: 'Tues, Jan 1, 2019', value: 'ddd, MMM d, yyyy' },
          { label: '1/10', value: '$/#' },
          { label: '1', value: '$' },
          { label: 'p.1', value: 'p.$' },
          { label: 'Page 1 of 10', value: 'Page $ of #' },
        ],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaMaxLength, parentName: this.metaValidation, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaOptions, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Multi',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaRadioType, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'check', value: 'check' },
          { label: 'circle', value: 'circle' },
          { label: 'disc', value: 'disc' },
        ],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaRadioLabel, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaValue, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaZIndex, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaBorderColor, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaBorderStyle, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: BORDER_STYLE,
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaBorderWidth, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaBGColor, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaFontSize, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaFontFamily, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: FONT_FAMILY,
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaFontColor, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaFontStyle, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'italic', value: 'italic' },
          { label: 'oblique', value: 'oblique' },
        ],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaFontWeight, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'normal', value: 'normal' },
          { label: 'bold', value: 'bold' },
          { label: 'lighter', value: 'lighter' },
          { label: 'bolder', value: 'bolder' },
          { label: '100', value: '100' },
          { label: '200', value: '200' },
          { label: '300', value: '300' },
          { label: '400', value: '400' },
          { label: '500', value: '500' },
          { label: '600', value: '600' },
          { label: '700', value: '700' },
          { label: '800', value: '800' },
          { label: '900', value: '900' },
        ],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaTextAlign, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'left', value: 'left' },
          { label: 'center', value: 'center' },
          { label: 'right', value: 'right' },
        ],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaVerticalAlign, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'top', value: 'top' },
          { label: 'middle', value: 'middle' },
          { label: 'bottom', value: 'bottom' },
        ],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaLineHeight, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        // ↑ CSSプロパティ「lineHight」は文字列値も受け付けられたような。。。
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaRequired, parentName: this.metaValidation, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaLabel, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaInputGroup, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      {
        name: this.metaPadding, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'TITLE'
      },
      // ImageInfo
      // {
      //  name: this.metaImageInfo, parentName: null, value: '',
      //  isFieldType: (type: FieldType): boolean => type === 'JSON',
      //  selectItems: [],
      //  isRequired: true,
      //  isEdited: false,
      //  category: 'IMAGEINFO'
      // },
      {
        name: this.metaLeft, parentName: this.metaImageInfo, value: '',
        isFieldType: (type: FieldType): boolean => type === 'JSON',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        category: 'IMAGEINFO'
      },
      {
        name: this.metaTop, parentName: this.metaImageInfo, value: '',
        isFieldType: (type: FieldType): boolean => type === 'JSON',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        category: 'IMAGEINFO'
      },
      {
        name: this.metaWidth, parentName: this.metaImageInfo, value: '',
        isFieldType: (type: FieldType): boolean => type === 'JSON',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        category: 'IMAGEINFO'
      },
      {
        name: this.metaHeight, parentName: this.metaImageInfo, value: '',
        isFieldType: (type: FieldType): boolean => type === 'JSON',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        category: 'IMAGEINFO'
      },
      {
        name: this.metaZIndex, parentName: this.metaImageInfo, value: '',
        isFieldType: (type: FieldType): boolean => type === 'JSON',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        category: 'IMAGEINFO'
      },
      {
        name: this.metaTitle, parentName: this.metaImageInfo, value: '',
        isFieldType: (type: FieldType): boolean => type === 'JSON',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        category: 'IMAGEINFO'
      },
      {
        name: this.metaImage, parentName: this.metaImageInfo, value: '',
        isFieldType: (type: FieldType): boolean => type === 'JSON',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        category: 'IMAGEINFO'
      }
    ];
  }

  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaTop:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleTop = this.units.concatenate('sizeUnit', metadata.value as number);
        } else if (metadata.parentName === this.metaImageInfo) {
          this.imageInfoTop = this.units.concatenate('sizeUnit', metadata.value as number);
        }
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleLeft = this.units.concatenate('sizeUnit', metadata.value as number);
        } else if (metadata.parentName === this.metaImageInfo) {
          this.imageInfoLeft = this.units.concatenate('sizeUnit', metadata.value as number);
        }
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleWidth = this.getWidth();
        } else if (metadata.parentName === this.metaImageInfo) {
          this.imageInfoWidth = this.units.concatenate('sizeUnit', metadata.value as number);
        }
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleHeight = this.units.concatenate('sizeUnit', this.baseDef.title.height);
        } else if (metadata.parentName === this.metaImageInfo) {
          this.imageInfoHeight = this.units.concatenate('sizeUnit', metadata.value as number);
        }
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleZIndex = metadata.value as number;
        } else if (metadata.parentName === this.metaImageInfo) {
          this.imageInfoZIndex = metadata.value as number;
        }
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderColor:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        if (metadata.parentName === this.metaTitle) {
          this.titleBorderColor = metadata.value as string;
        }
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderStyle:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        if (metadata.parentName === this.metaTitle) {
          this.titleBorderStyle = metadata.value as string;
        }
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderWidth:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleBorderWidth = this.units.concatenate('sizeUnit', metadata.value as number);
        }
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTextAlign:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        if (metadata.parentName === this.metaTitle) {
          this.titleTextAlign = metadata.value as string;
        }
        this.setPreviewValue(metadata.name, metadata.value);
        break;

      case this.metaBGColor:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleBackgroundColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaType:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleText = this.getText(this.baseDef.title);
        this.setPreviewValue(metadata.name, metadata.value);
        if (metadata.value === 'static') {
          this.setPreviewValue(this.metaDynamicId, null);
          this.baseDef[this.metaDynamicId] = null;
          this.setPreviewValue(this.metaEditableId, null);
          this.baseDef[this.metaEditableId] = null;
          this.setPreviewValue(this.metaFormat, null);
          this.baseDef[this.metaFormat] = null;
        } else if (metadata.value === 'dynamic') {
          this.setPreviewValue(this.metaText, '');
          this.baseDef[this.metaText] = '';
          this.setPreviewValue(this.metaEditableId, null);
          this.baseDef[this.metaEditableId] = null;
        }
        break;
      case this.metaText:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleText = this.getText(this.baseDef.title);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFormat:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontSize:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleFontSize = this.fonts.getFontSize(this.baseDef.title);
          this.setPreviewValue(metadata.name, metadata.value);
        }
        break;
      case this.metaFontFamily:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        if (metadata.parentName === this.metaTitle) {
          this.titleFontFamily = this.fonts.getFontFamily(this.baseDef.title);
          this.setPreviewValue(metadata.name, metadata.value);
        }
        break;
      case this.metaFontColor:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleFontColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontStyle:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleFontStyle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontWeight:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleFontWeight = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaVerticalAlign:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleVerticalAlign = metadata.value as string;
        this.titleAlignItems = this.getAlignItems();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaIsHorizontal:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as boolean;
        this.titleIsHorizontal = metadata.value as boolean;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLineHeight:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        this.titleLineHeight = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaDynamicId:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleText = this.getText(this.baseDef.title);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaInputType:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        if (metadata.value !== 'select') {
          this.setPreviewValue(this.metaOptions, '[]');
          this.baseDef[this.metaOptions] = [];
        }
        if (metadata.value !== 'radio') {
          this.setPreviewValue(this.metaRadioType, null);
          this.baseDef[this.metaRadioType] = null;
          this.setPreviewValue(this.metaRadioLabel, null);
          this.baseDef[this.metaRadioLabel] = null;
          this.setPreviewValue(this.metaValue, null);
          this.baseDef[this.metaValue] = null;
        }
        break;
      case this.metaEditableId:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleText = this.getText(this.baseDef.title);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaRequired:
        if (!this.baseDef[this.metaValidation]) {
          this.baseDef[this.metaValidation] = { required: false, maxLength: null };
        }
        this.baseDef[this.metaValidation][metadata.name] = metadata.value as boolean;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaMaxLength:
        if (!this.baseDef[this.metaValidation]) {
          this.baseDef[this.metaValidation] = { required: false, maxLength: null };
        }
        this.baseDef[this.metaValidation][metadata.name] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaRadioType:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaRadioLabel:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaOptions:
        this.baseDef[metadata.parentName][metadata.name] = JSON.parse(metadata.value as string);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLabel:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaValue:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaInputGroup:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaPadding:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      // case this.metaImageInfo:
      //  this.baseDef[metadata.name] = JSON.parse(metadata.value as string);
      //  this.imageInfoTop = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaTop] as number);
      //  this.imageInfoLeft = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaLeft] as number);
      //  this.imageInfoWidth = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaWidth] as number);
      //  this.imageInfoHeight = this.units.concatenate('sizeUnit', this.baseDef[metadata.name][this.metaHeight] as number);
      //  this.imageInfoTitle = this.baseDef[metadata.name][this.metaTitle];
      //  this.imageInfoImage = this.baseDef[metadata.name][this.metaImage]
      //  this.setPreviewValue(metadata.name, metadata.value);
      //  break;
      case this.metaTitle:
        if (metadata.parentName === this.metaImageInfo) {
          try {
            this.baseDef[metadata.parentName][metadata.name] = JSON.parse(metadata.value as string);
          } catch (error) {
            this.baseDef[metadata.parentName][metadata.name] = metadata.value as TemplateDataPlateText;
          }
          this.imageInfoTitleTop = this.units.concatenate('sizeUnit',
            this.baseDef[metadata.parentName][metadata.name][this.metaTop] as number);
          this.imageInfoTitleLeft = this.units.concatenate('sizeUnit',
            this.baseDef[metadata.parentName][metadata.name][this.metaLeft] as number);
          this.imageInfoTitleWidth = this.units.concatenate('sizeUnit',
            this.baseDef[metadata.parentName][metadata.name][this.metaWidth] as number);
          this.imageInfoTitleHeight = this.units.concatenate('sizeUnit',
            this.baseDef[metadata.parentName][metadata.name][this.metaHeight] as number);
          this.imageInfoTitleZIndex = this.baseDef[metadata.parentName][metadata.name][this.metaZIndex] as number;
          this.imageInfoTitleText = this.baseDef[metadata.parentName][metadata.name][this.metaText];
          this.setPreviewValue(metadata.name, metadata.value);
        }
        break;
      case this.metaImage:
        if (metadata.parentName === this.metaImageInfo) {
          try {
            this.baseDef[metadata.parentName][metadata.name] = JSON.parse(metadata.value as string);
          } catch (error) {
            this.baseDef[metadata.parentName][metadata.name] = metadata.value as TemplateImage;
          }
          this.imageInfoImageTop = this.units.concatenate('sizeUnit',
            this.baseDef[metadata.parentName][metadata.name][this.metaTop] as number);
          this.imageInfoImageLeft = this.units.concatenate('sizeUnit',
            this.baseDef[metadata.parentName][metadata.name][this.metaLeft] as number);
          this.imageInfoImageWidth = this.units.concatenate('sizeUnit',
            this.baseDef[metadata.parentName][metadata.name][this.metaWidth] as number);
          this.imageInfoImageHeight = this.units.concatenate('sizeUnit',
            this.baseDef[metadata.parentName][metadata.name][this.metaHeight] as number);
          this.imageInfoImageZIndex = this.baseDef[metadata.parentName][metadata.name][this.metaZIndex] as number;

          this.imageInfoImageCaption = this.baseDef[metadata.parentName][metadata.name][this.metaCaption];
          this.imageInfoImageSrc = this.baseDef[metadata.parentName][metadata.name][this.metaSrc];
          this.imageInfoImageType = this.baseDef[metadata.parentName][metadata.name][this.metaType];
          this.imageInfoImageOrientation = this.baseDef[metadata.parentName][metadata.name][this.metaOrientaion];

          this.setPreviewValue(metadata.name, metadata.value);
        }
        break;
    }
  }

  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
  }

  private getHeightNum(): number {
    let rtn = this.paperSize.getHeightNum();
    rtn = this.pageMargin.getPaddedHeightNum(rtn);
    rtn = rtn - this.header.getHeightNum() - this.footer.getHeightNum() - (this.baseDef.y * 2);
    rtn = Math.floor(rtn);
    return rtn;
  }

  private getHeight(): string {
    return [this.getHeightNum(), 'mm'].join('');
  }

  private getWidthNum(): number {
    let rtn = this.paperSize.getWidthNum();
    rtn = this.pageMargin.getPaddedWidthNum(rtn) - (this.baseDef.title.x * 2);
    rtn = Math.floor(rtn);
    return rtn;
  }
  private getWidth(): string {
    return [this.getWidthNum(), 'mm'].join('');
  }

  private getText(text: TemplateText): string {
    let rtnText = '';
    switch (text.type) {
      case 'static':
        rtnText = text.text;
        break;
      case 'dynamic':
        if (text.dynamicDataField) {
          rtnText = '[' + text.dynamicDataField + ']';
        } else if (text.dynamicId) {
          rtnText = '[' + text.dynamicId + ']';
        } else {
          rtnText = '[Dynamic Text]';
        }
        break;
      case 'dynamic-editable':
        rtnText = text.editableId ? '{' + text.editableId + '}' : '{Editable Text}';
        break;
    }
    return rtnText;
  }

  private getJustifyContent(): string {
    let rtn: string;
    switch (this.baseDef.title.horizontalAlign) {
      case 'left':
        rtn = 'flex-start';
        break;
      case 'center':
        rtn = 'center';
        break;
      case 'right':
        rtn = 'flex-end';
        break;
    }
    return rtn;
  }

  private getAlignItems(): string {
    let rtn: string;
    switch (this.baseDef.title.verticalAlign) {
      case 'top':
        rtn = 'flex-start';
        break;
      case 'middle':
        rtn = 'center';
        break;
      case 'bottom':
        rtn = 'flex-end';
        break;
    }
    return rtn;
  }

}

/** *********************************************************************/
class PreviewControllerOfSection {
  private readonly textIds: Array<string>;
  private readonly lineIds: Array<string>;
  private readonly imageIds: Array<string>;
  private readonly rectangleIds: Array<string>;
  private readonly summaryIds: Array<string>;
  private readonly pictureIds: Array<string>;
  private readonly dataPlateIds: Array<string>;

  /**
   * コンストラクタ
   */
  constructor() {
    this.textIds = new Array<string>();
    this.lineIds = new Array<string>();
    this.imageIds = new Array<string>();
    this.rectangleIds = new Array<string>();
    this.summaryIds = new Array<string>();
    this.pictureIds = new Array<string>();
    this.dataPlateIds = new Array<string>();
  }

  /**
   * キー配列の取得
   * @param type どの項目かを示す値
   */
  public getIds(type: ElementType): Array<string> {
    return this.getCore(type);
  }

  /**
   * レポート項目を描画する
   * @param type 描画する項目の種類
   * @param id 描画する項目のID値
   */
  public draw(type: ElementType, id: string | Array<string>): void;
  public draw(type: ElementType, id: any): void {

    const typeOf = Object.prototype.toString;
    const ids: Array<string> = (typeOf.call(id) === '[object Array]') ? id : [id];

    ids.forEach((key: string) => {
      if (this.getCore(type).indexOf(key) === -1) {
        this.getCore(type).push(key);
      } else {
        // console.log('this key is already exist. key: %s', key);
      }
    });
  }

  /**
   * レポート項目を削除する
   * @param id 削除する項目のID値
   */
  public remove(id: string | Array<string>): void;
  public remove(id: any): void {

    // 複数の配列に対して同じ処理を行うため、実装ミスを防ぐ目的で
    // functionオブジェクトを生成
    const worker = function (target: Array<string>, elementId: any) {
      let result = 0;
      const typeOf = Object.prototype.toString;
      const ids: Array<string> = typeOf.call(elementId) === '[object Array]' ? elementId : [elementId];

      // １つの配列に同じキー値が複数含まれる可能性は、ゼロではないと思い、
      // 指定されたキー値が見つからなくなるまで（result が -1になるまで）削除処理を繰り返す
      ids.forEach((key: string, index: number) => {
        while (result !== -1) {
          result = target.indexOf(key);
          if (result !== -1) {
            target.splice(result, 1);
          }
        }
      });
    };

    worker(this.textIds, id);
    worker(this.lineIds, id);
    worker(this.imageIds, id);
    worker(this.rectangleIds, id);
    worker(this.summaryIds, id);
    worker(this.pictureIds, id);
    worker(this.dataPlateIds, id);
  }

  /**
   * 指定された種類の項目全てを削除する
   * @param type 削除する項目の種類
   */
  public removeAll(type: ElementType): void {
    const length = this.getCore(type).length;
    this.getCore(type).splice(0, length);
  }

  private getCore(type: ElementType): Array<string> {
    let rtnArray: Array<string>;
    switch (type) {
      case ElementType.Text:
        rtnArray = this.textIds;
        break;
      case ElementType.Line:
        rtnArray = this.lineIds;
        break;
      case ElementType.Image:
        rtnArray = this.imageIds;
        break;
      case ElementType.Rectangle:
        rtnArray = this.rectangleIds;
        break;
      case ElementType.Summary:
        rtnArray = this.summaryIds;
        break;
      case ElementType.Picture:
        rtnArray = this.pictureIds;
        break;
      case ElementType.DataPlate:
        rtnArray = this.dataPlateIds;
        break;
      default:
        // NOP
        break;
    }
    return rtnArray;
  }
}

/** *********************************************************************/
class PreviewControllerOfPage {
  private readonly headerController: PreviewControllerOfSection;
  private readonly bodyController: PreviewControllerOfSection;
  private readonly footerController: PreviewControllerOfSection;

  /**
   * コンストラクタ
   */
  constructor() {
    this.headerController = new PreviewControllerOfSection();
    this.bodyController = new PreviewControllerOfSection();
    this.footerController = new PreviewControllerOfSection();
  }

  /**
   * 指定した領域・項目のID値を返却する
   * @param location どの領域かを示す値
   * @param type どの項目かを示す値
   */
  public getIds(location: Location, type: ElementType): Array<string> {
    return this.getCore(location).getIds(type);
  }

  /**
   * レポートプレビューに描画する
   * @param location どのセクションかを示す値
   * @param type どの項目かを示す値
   * @param id 描画する項目のキー値
   */
  public draw(location: Location, type: ElementType, id: string | Array<string>): void;
  public draw(location: Location, type: ElementType, id: any): void {
    this.getCore(location).draw(type, id);
  }

  /**
   * レポートプレビューに描画済みの項目を移動する
   * @param type どの項目かを示す値
   * @param id 移動対象となる項目のID値
   * @param fromTo どのセクションから（=from）どのセクションへ（=to）移動するかを示す値
   */
  public move(type: ElementType, id: string, fromTo: FromTo): void {
    this.getCore(fromTo.from.location).remove(id);
    this.getCore(fromTo.to.location).draw(type, id);
  }

  /**
   * レポートプレビューから項目を削除する
   * @param location どのセクションかを示す値
   * @param id 削除する項目のID値
   */
  public remove(location: Location, id: string | Array<string>): void;
  public remove(location: Location, id: any): void {
    this.getCore(location).remove(id);
  }

  /**
   * レポートプレビューから指定された項目をすべて削除する
   * @param location どのセクションかを示す値
   * @param type どの項目かを示す値
   */
  public removeAll(location: Location, type: ElementType): void {
    this.getCore(location).removeAll(type);
  }

  private getCore(location: Location): PreviewControllerOfSection {
    let rtn: PreviewControllerOfSection;
    switch (location) {
      case Location.Header:
        rtn = this.headerController;
        break;
      case Location.Body:
        rtn = this.bodyController;
        break;
      case Location.Footer:
        rtn = this.footerController;
        break;
      default:
        // NOP
        break;
    }
    return rtn;
  }
}

/** *********************************************************************/
class PreviewController {
  private readonly pageIds: Array<string>;
  private readonly pageController: { [pageId: string]: PreviewControllerOfPage };

  constructor() {
    this.pageIds = new Array<string>();
    this.pageController = {};
  }

  public getPageIds(): Array<string> {
    return this.pageIds;
  }
  public getIds(pageId: string, location: Location, type: ElementType): Array<string> {
    return this.pageController[pageId].getIds(location, type);
  }

  public addPage(pageId: string | Array<string>): void;
  public addPage(pageId: any): void {

    const typeOf = Object.prototype.toString;
    const ids = typeOf.call(pageId) === '[object Array]' ? pageId : [pageId];

    ids.forEach((id: string) => {
      this.pageIds.push(pageId);
      this.pageController[pageId] = new PreviewControllerOfPage();
    });
  }

  public removePage(pageId: string | Array<string>): void;
  public removePage(pageId: any): void {

    const typeOf = Object.prototype.toString;
    const pageIds: Array<string> = typeOf.call(pageId) === '[object Array]' ? pageId : [pageId];

    // pageKeys配列から当該キーを削除
    pageIds.forEach((id: string) => {
      const result = this.pageIds.indexOf(id);
      if (result !== -1) {
        this.pageIds.splice(result, 1);
      }
    });

    // previewから当該キーと紐付くオブジェクトを削除
    pageIds.forEach((id: string) => {
      delete this.pageController[id];
    });
  }

  public removeAllPage() {
    // pageKeys配列から全てのキーを削除
    const length = this.pageIds.length;
    this.pageIds.splice(0, length);

    // previewから全てのプロパティ（オブジェクト）を削除
    Object.keys(this.pageController).forEach((propertyName: string) => {
      delete this.pageController[propertyName];
    });
  }

  public draw(pageId: string, location: Location, type: ElementType, id: string | Array<string>);
  public draw(pageId: string, location: Location, type: ElementType, id: any) {
    this.pageController[pageId].draw(location, type, id);
  }

  public remove(pageId: string, location: Location, id: string | Array<string>);
  public remove(pageId: string, location: Location, id: any) {
    this.pageController[pageId].remove(location, id);
  }

  public removeAll(pageId: string, location: Location, type: ElementType) {
    this.pageController[pageId].removeAll(location, type);
  }

  public move(id: string, type: ElementType, fromTo: FromTo) {
    const from = fromTo.from;
    const to = fromTo.to;
    this.pageController[from.pageId].remove(from.location, id);
    this.pageController[to.pageId].draw(to.location, type, id);
  }

  public redrawHeader(
    pageId: string, hasHeader: boolean,
    newTextIds: Array<string>,
    newLineIds: Array<string>,
    newImageIds: Array<string>,
    newRectangleIds: Array<string>,
    newSummaryIds: Array<string>,
    newPictureIds: Array<string>,
    newDataPlateIds: Array<string>
  ) {
    this.removeAll(pageId, Location.Header, ElementType.Text);
    this.removeAll(pageId, Location.Header, ElementType.Line);
    this.removeAll(pageId, Location.Header, ElementType.Image);
    this.removeAll(pageId, Location.Header, ElementType.Rectangle);
    this.removeAll(pageId, Location.Header, ElementType.Summary);
    this.removeAll(pageId, Location.Header, ElementType.Picture);
    this.removeAll(pageId, Location.Header, ElementType.DataPlate);
    if (hasHeader) {
      this.draw(pageId, Location.Header, ElementType.Text, newTextIds);
      this.draw(pageId, Location.Header, ElementType.Line, newLineIds);
      this.draw(pageId, Location.Header, ElementType.Image, newImageIds);
      this.draw(pageId, Location.Header, ElementType.Rectangle, newRectangleIds);
      this.draw(pageId, Location.Header, ElementType.Summary, newSummaryIds);
      this.draw(pageId, Location.Header, ElementType.Picture, newPictureIds);
      this.draw(pageId, Location.Header, ElementType.DataPlate, newDataPlateIds);
    }
  }

  public redrawFooter(
    pageId: string, hasFooer: boolean,
    newTextIds: Array<string>,
    newLineIds: Array<string>,
    newImageIds: Array<string>,
    newRectangleIds: Array<string>,
    newSummaryIds: Array<string>,
    newPictureIds: Array<string>,
    newDataPlateIds: Array<string>
  ) {
    this.removeAll(pageId, Location.Footer, ElementType.Text);
    this.removeAll(pageId, Location.Footer, ElementType.Line);
    this.removeAll(pageId, Location.Footer, ElementType.Image);
    this.removeAll(pageId, Location.Footer, ElementType.Rectangle);
    this.removeAll(pageId, Location.Footer, ElementType.Summary);
    this.removeAll(pageId, Location.Footer, ElementType.Picture);
    this.removeAll(pageId, Location.Footer, ElementType.DataPlate);
    if (hasFooer) {
      this.draw(pageId, Location.Footer, ElementType.Text, newTextIds);
      this.draw(pageId, Location.Footer, ElementType.Line, newLineIds);
      this.draw(pageId, Location.Footer, ElementType.Image, newImageIds);
      this.draw(pageId, Location.Footer, ElementType.Rectangle, newRectangleIds);
      this.draw(pageId, Location.Footer, ElementType.Summary, newSummaryIds);
      this.draw(pageId, Location.Footer, ElementType.Picture, newPictureIds);
      this.draw(pageId, Location.Footer, ElementType.DataPlate, newDataPlateIds);
    }
  }

  public redrawBody(
    pageId: string,
    newTextIds: Array<string>,
    newLineIds: Array<string>,
    newImageIds: Array<string>,
    newRectangleIds: Array<string>,
    newSummaryIds: Array<string>,
    newPictureIds: Array<string>,
    newDataPlateIds: Array<string>
  ) {
    this.removeAll(pageId, Location.Body, ElementType.Text);
    this.removeAll(pageId, Location.Body, ElementType.Line);
    this.removeAll(pageId, Location.Body, ElementType.Image);
    this.removeAll(pageId, Location.Body, ElementType.Rectangle);
    this.removeAll(pageId, Location.Body, ElementType.Summary);
    this.removeAll(pageId, Location.Body, ElementType.Picture);
    this.removeAll(pageId, Location.Body, ElementType.DataPlate);
    this.draw(pageId, Location.Body, ElementType.Text, newTextIds);
    this.draw(pageId, Location.Body, ElementType.Line, newLineIds);
    this.draw(pageId, Location.Body, ElementType.Image, newImageIds);
    this.draw(pageId, Location.Body, ElementType.Rectangle, newRectangleIds);
    this.draw(pageId, Location.Body, ElementType.Summary, newSummaryIds);
    this.draw(pageId, Location.Body, ElementType.Picture, newPictureIds);
    this.draw(pageId, Location.Body, ElementType.DataPlate, newDataPlateIds);
  }

}

/** *********************************************************************/
export class ReportModel {

  private units: ReportUnits;
  private fonts: ReportFonts;
  private paperSize: ReportPaperSize;
  private pageMargin: ReportPageMargin;
  private pageHeader: ReportCommonSection;
  private pageFooter: ReportCommonSection;

  private pages: { [elementId: string]: ReportPage };

  private headerTexts: { [elementId: string]: ReportText };
  private headerLines: { [elementId: string]: ReportLine };
  private headerImages: { [elementId: string]: ReportImage };
  private headerRectangles: { [elementId: string]: ReportRectangle };
  private headerSummaries: { [elementId: string]: ReportSummary };
  private headerPictures: { [elementId: string]: ReportSummary };
  private headerDataPlates: { [elementId: string]: ReportDataPlate };

  private footerTexts: { [elementId: string]: ReportText };
  private footerLines: { [elementId: string]: ReportLine };
  private footerImages: { [elementId: string]: ReportImage };
  private footerRectangles: { [elementId: string]: ReportRectangle };
  private footerSummaries: { [elementId: string]: ReportSummary };
  private footerPictures: { [elementId: string]: ReportSummary };
  private footerDataPlates: { [elementId: string]: ReportDataPlate };

  private bodyTexts: { [elementId: string]: ReportText };
  private bodyLines: { [elementId: string]: ReportLine };
  private bodyImages: { [elementId: string]: ReportImage };
  private bodyRectangles: { [elementId: string]: ReportRectangle };
  private bodySummaries: { [elementId: string]: ReportSummary };
  private bodyPictures: { [elementId: string]: ReportSummary };
  private bodyDataPlates: { [elementId: string]: ReportDataPlate };

  private elementFinder: { [key: string]: ReportElement };
  // private mapFinder: { [location: string]: { [type: string]: Object } };

  get ElementFinder(): { [key: string]: ReportElement} {
    return this.elementFinder;
  }

  private previewController: PreviewController;

  constructor(templateDef: TemplateDefinition) {

    this.units = new ReportUnits(templateDef);
    this.fonts = new ReportFonts(templateDef, this.units);
    this.paperSize = new ReportPaperSize(templateDef);
    this.pageMargin = new ReportPageMargin(templateDef);
    this.pageHeader = new ReportCommonSection('Page Header', templateDef.header);
    this.pageFooter = new ReportCommonSection('Page Footer', templateDef.footer);

    this.pages = {};

    this.headerTexts = {};
    this.headerLines = {};
    this.headerImages = {};
    this.headerRectangles = {};
    this.headerSummaries = {};
    this.headerPictures = {};
    this.headerDataPlates = {};

    this.bodyTexts = {};
    this.bodyLines = {};
    this.bodyImages = {};
    this.bodyRectangles = {};
    this.bodySummaries = {};
    this.bodyPictures = {};
    this.bodyDataPlates = {};

    this.footerTexts = {};
    this.footerLines = {};
    this.footerImages = {};
    this.footerRectangles = {};
    this.footerSummaries = {};
    this.footerPictures = {};
    this.footerDataPlates = {};

    this.elementFinder = {};

    this.previewController = new PreviewController();

    // Header項目の初期化
    this.elementFactory<TemplateText, ReportText>(
      this.headerTexts, this.textCreator.bind(this), Location.Header,
      templateDef.header.texts
    );
    this.elementFactory<TemplateLine, ReportLine>(
      this.headerLines, this.lineCreator.bind(this), Location.Header,
      templateDef.header.lines
    );
    this.elementFactory<TemplateImage, ReportImage>(
      this.headerImages, this.imageCreator.bind(this), Location.Header,
      templateDef.header.images
    );
    this.elementFactory<TemplateRectangle, ReportRectangle>(
      this.headerRectangles, this.rectangleCreator.bind(this), Location.Header,
      templateDef.header.rectangles
    );
    if (templateDef.header.inspectionSummary) {
      this.elementFactory<TemplateSummary, ReportSummary>(
        this.headerSummaries, this.summaryCreator.bind(this), Location.Header,
        [templateDef.header.inspectionSummary]
      );
    }
    if (templateDef.header.inspectionPictures) {
      this.elementFactory<TemplateSummary, ReportSummary>(
        this.headerPictures, this.pictureCreator.bind(this), Location.Header,
        [templateDef.header.inspectionPictures]
      );
    }
    if (templateDef.header.dataPlates) {
      this.elementFactory<TemplateDataPlate, ReportDataPlate>(
        this.headerDataPlates, this.dataPlateCreator.bind(this), Location.Header,
        [templateDef.header.dataPlates]
      );
    }

    // Footer項目の初期化
    this.elementFactory<TemplateText, ReportText>(
      this.footerTexts, this.textCreator.bind(this), Location.Footer,
      templateDef.footer.texts
    );
    this.elementFactory<TemplateLine, ReportLine>(
      this.footerLines, this.lineCreator.bind(this), Location.Footer,
      templateDef.footer.lines
    );
    this.elementFactory<TemplateImage, ReportImage>(
      this.footerImages, this.imageCreator.bind(this), Location.Footer,
      templateDef.footer.images
    );
    this.elementFactory<TemplateRectangle, ReportRectangle>(
      this.footerRectangles, this.rectangleCreator.bind(this), Location.Footer,
      templateDef.footer.rectangles
    );
    if (templateDef.footer.inspectionSummary) {
      this.elementFactory<TemplateSummary, ReportSummary>(
        this.footerSummaries, this.summaryCreator.bind(this), Location.Footer,
        [templateDef.footer.inspectionSummary]
      );
    }
    if (templateDef.footer.inspectionPictures) {
      this.elementFactory<TemplateSummary, ReportSummary>(
        this.footerPictures, this.pictureCreator.bind(this), Location.Footer,
        [templateDef.footer.inspectionPictures]
      );
    }
    if (templateDef.footer.dataPlates) {
      this.elementFactory<TemplateDataPlate, ReportDataPlate>(
        this.footerDataPlates, this.dataPlateCreator.bind(this), Location.Footer,
        [templateDef.footer.dataPlates]
      );
    }

    // Pageの初期化
    this.elementFactory<TemplatePageSection, ReportPage>(
      this.pages, this.pageCreator.bind(this), Location.Page,
      templateDef.bodies
    );

    // Page上の項目の初期化
    for (const pageId of Object.keys(this.pages)) {
      const page = this.pages[pageId];
      const pageIndex = page.getIndex();
      this.elementFactoryOnPage<TemplateText, ReportText>(
        this.bodyTexts, this.textCreator.bind(this), Location.Body, pageIndex, pageId,
        templateDef.bodies[pageIndex].texts
      );
      this.elementFactoryOnPage<TemplateLine, ReportLine>(
        this.bodyLines, this.lineCreator.bind(this), Location.Body, pageIndex, pageId,
        templateDef.bodies[pageIndex].lines,
      );
      this.elementFactoryOnPage<TemplateImage, ReportImage>(
        this.bodyImages, this.imageCreator.bind(this), Location.Body, pageIndex, pageId,
        templateDef.bodies[pageIndex].images
      );
      this.elementFactoryOnPage<TemplateRectangle, ReportRectangle>(
        this.bodyRectangles, this.rectangleCreator.bind(this), Location.Body, pageIndex, pageId,
        templateDef.bodies[pageIndex].rectangles
      );

      if (templateDef.bodies[pageIndex].inspectionSummary) {
        this.elementFactoryOnPage<TemplateSummary, ReportSummary>(
          this.bodySummaries, this.summaryCreator.bind(this), Location.Body, pageIndex, pageId,
          [templateDef.bodies[pageIndex].inspectionSummary]
        );
      }
      if (templateDef.bodies[pageIndex].inspectionPictures) {
        this.elementFactoryOnPage<TemplateSummary, ReportSummary>(
          this.bodyPictures, this.pictureCreator.bind(this), Location.Body, pageIndex, pageId,
          [templateDef.bodies[pageIndex].inspectionPictures]
        );
      }
      if (templateDef.bodies[pageIndex].dataPlates) {
        this.elementFactoryOnPage<TemplateDataPlate, ReportDataPlate>(
          this.bodyDataPlates, this.dataPlateCreator.bind(this), Location.Body, pageIndex, pageId,
          [templateDef.bodies[pageIndex].dataPlates]
        );
      }
    }

    // ReportPreviewModelの初期化
    for (const pageId of Object.keys(this.pages)) {

      const page = this.pages[pageId];

      this.previewController.addPage(pageId);

      this.previewController.redrawHeader(
        pageId, page.hasHeader,
        Object.keys(this.headerTexts),
        Object.keys(this.headerLines),
        Object.keys(this.headerImages),
        Object.keys(this.headerRectangles),
        Object.keys(this.headerSummaries),
        Object.keys(this.headerPictures),
        Object.keys(this.headerDataPlates)
      );

      this.previewController.redrawFooter(
        pageId, page.hasFooter,
        Object.keys(this.footerTexts),
        Object.keys(this.footerLines),
        Object.keys(this.footerImages),
        Object.keys(this.footerRectangles),
        Object.keys(this.footerSummaries),
        Object.keys(this.footerPictures),
        Object.keys(this.footerDataPlates)
      );

      this.previewController.redrawBody(
        pageId,
        Object.keys(this.instanceMapFilter(this.bodyTexts, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyLines, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyImages, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyRectangles, pageId)),
        Object.keys(this.instanceMapFilter(this.bodySummaries, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyPictures, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyDataPlates, pageId)),
      );
    }
  }

  public getPageIds(): Array<string> {
    // Angularのオートバインディングを用いてレポート要素の再描画を行うため
    // previewControllerが保持する要素ID配列のインスタンスを返却する
    return this.previewController.getPageIds();
  }
  public getIds(pageId: string, location: Location, type: ElementType): Array<string> {
    // Angularのオートバインディングを用いてレポート要素の再描画を行うため
    // previewControllerが保持する要素ID配列のインスタンスを返却する
    return this.previewController.getIds(pageId, location, type);
  }
  public removeAllPage() {
    return this.previewController.removeAllPage();
  }

  public getElement(id: string): ReportElement {
    return this.elementFinder[id];
  }
  public getElements(ids: string[]): ReportElement[] {
    const rtnArray = new Array<ReportElement>();
    for (const id of ids) {
      rtnArray.push(this.getElement(id));
    }
    return rtnArray;
  }
  public getHeaderTexts(): { [elementId: string]: ReportText } {
    return this.headerTexts;
  }
  public getHeaderLines(): { [elementId: string]: ReportLine } {
    return this.headerLines;
  }
  public getHeaderImages(): { [elementId: string]: ReportImage } {
    return this.headerImages;
  }
  public getHeaderRectangles(): { [elementId: string]: ReportRectangle } {
    return this.headerRectangles;
  }

  public getFooterTexts(): { [elementId: string]: ReportText } {
    return this.footerTexts;
  }
  public getFooterLines(): { [elementId: string]: ReportLine } {
    return this.footerLines;
  }
  public getFooterImages(): { [elementId: string]: ReportImage } {
    return this.footerImages;
  }
  public getFooterRectangles(): { [elementId: string]: ReportRectangle } {
    return this.footerRectangles;
  }

  public getBodyTexts(): { [elementId: string]: ReportText } {
    return this.bodyTexts;
  }
  public getBodyLines(): { [elementId: string]: ReportLine } {
    return this.bodyLines;
  }
  public getBodyImages(): { [elementId: string]: ReportImage } {
    return this.bodyImages;
  }
  public getBodyRectangles(): { [elementId: string]: ReportRectangle } {
    return this.bodyRectangles;
  }

  public getPage(id: string): ReportPage {
    return this.pages[id];
  }
  public getPages(ids: string[]): ReportPage[] {
    const rtnArray = new Array<ReportPage>();
    for (const id of ids) {
      rtnArray.push(this.getPage(id));
    }
    return rtnArray;
  }

  public getText(id: string): ReportText {
    return this.elementFinder[id] as ReportText;
  }
  public getTexts(ids: string[]): ReportText[] {
    const rtnArray = new Array<ReportText>();
    for (const id of ids) {
      rtnArray.push(this.getText(id));
    }
    return rtnArray;
  }

  public getLine(id: string): ReportLine {
    return this.elementFinder[id] as ReportLine;
  }
  public getLines(ids: string[]): ReportLine[] {
    const rtnArray = new Array<ReportLine>();
    for (const id of ids) {
      rtnArray.push(this.getLine(id));
    }
    return rtnArray;
  }

  public getImage(id: string): ReportImage {
    return this.elementFinder[id] as ReportImage;
  }
  public getImages(ids: string[]): ReportImage[] {
    const rtnArray = new Array<ReportImage>();
    for (const id of ids) {
      rtnArray.push(this.getImage(id));
    }
    return rtnArray;
  }

  public getRectangle(id: string): ReportRectangle {
    return this.elementFinder[id] as ReportRectangle;
  }
  public getRectangles(ids: string[]): ReportRectangle[] {
    const rtnArray = new Array<ReportRectangle>();
    for (const id of ids) {
      rtnArray.push(this.getRectangle(id));
    }
    return rtnArray;
  }

  public getSummary(id: string): ReportSummary {
    return this.elementFinder[id] as ReportSummary;
  }
  public getSummaries(ids: string[]): ReportSummary[] {
    const rtnArray = new Array<ReportSummary>();
    for (const id of ids) {
      rtnArray.push(this.getSummary(id));
    }
    return rtnArray;
  }

  public getDataPlate(id: string): ReportDataPlateOffline {
    return this.elementFinder[id] as ReportDataPlateOffline;
  }
  public getDataPlates(ids: string[]): ReportDataPlateOffline[] {
    const rtnArray = new Array<ReportDataPlateOffline>();
    for (const id of ids) {
      rtnArray.push(this.getDataPlate(id));
    }
    return rtnArray;
  }

  public getMeasurementPoint(id: string): ReportMeasurementPoint {
    return this.elementFinder[id] as ReportMeasurementPoint;
  }
  public getMeasurementPoints(ids: string[]): ReportMeasurementPoint[] {
    const rtnArray = new Array<ReportMeasurementPoint>();
    for (const id of ids) {
      rtnArray.push(this.getMeasurementPoint(id));
    }
    return rtnArray;
  }

  public getVisualEvent(id: string): ReportVisualEvents {
    return this.elementFinder[id] as ReportVisualEvents;
  }
  public getVisualEvents(ids: string[]): ReportVisualEvents[] {
    const rtnArray = new Array<ReportVisualEvents>();
    for (const id of ids) {
      rtnArray.push(this.getVisualEvent(id));
    }
    return rtnArray;
  }

  public getSignature(id: string): ReportSignature {
    return this.elementFinder[id] as ReportSignature;
  }
  public getSignatures(ids: string[]): ReportSignature[] {
    const rtnArray = new Array<ReportSignature>();
    for (const id of ids) {
      rtnArray.push(this.getSignature(id));
    }
    return rtnArray;
  }

  public getSection(id: string): ReportSection {
    return this.elementFinder[id] as ReportSection;
  }
  public getSections(ids: string[]): ReportSection[] {
    const rtnArray = new Array<ReportSection>();
    for (const id of ids) {
      rtnArray.push(this.getSection(id));
    }
    return rtnArray;
  }

  public getTable(id: string): ReportTable {
    return this.elementFinder[id] as ReportTable;
  }
  public getTables(ids: string[]): ReportTable[] {
    const rtnArray = new Array<ReportTable>();
    for (const id of ids) {
      rtnArray.push(this.getTable(id));
    }
    return rtnArray;
  }

  public getInterActiveIso(id: string): ReportISO {
    return this.elementFinder[id] as ReportISO;
  }
  public getInterActiveIsos(ids: string[]): ReportISO[] {
    const rtnArray = new Array<ReportISO>();
    for (const id of ids) {
      rtnArray.push(this.getInterActiveIso(id));
    }
    return rtnArray;
  }

  public getDropDownList(id: string): ReportDropDownList {
    return this.elementFinder[id] as ReportDropDownList;
  }
  public getDropDownLists(ids: string[]): ReportDropDownList[] {
    return ids.map((id) => this.getDropDownList(id));
  }

  public getCustomElement(id: string): ReportCustomElement {
    return this.elementFinder[id] as ReportCustomElement;
  }
  public getCustomElements(ids: string[]): ReportCustomElement[] {
    const rtnArray = new Array<ReportCustomElement>();
    for (const id of ids) {
      rtnArray.push(this.getCustomElement(id));
    }
    return rtnArray;
  }

  public addPage(): void {

    const createdIds = this.elementFactory<TemplatePageSection, ReportPage>(
      this.pages, this.pageCreator.bind(this), Location.Page
    );

    for (const pageId of createdIds) {
      // 新規作成分は選択済みにしておく
      this.pages[pageId].isSelected = true;

      // プレビュー表示を更新する
      this.previewController.addPage(pageId);

      this.previewController.redrawHeader(
        pageId, this.pages[pageId].hasHeader,
        Object.keys(this.headerTexts),
        Object.keys(this.headerLines),
        Object.keys(this.headerImages),
        Object.keys(this.headerRectangles),
        Object.keys(this.headerSummaries),
        Object.keys(this.headerPictures),
        Object.keys(this.headerDataPlates)
      );

      this.previewController.redrawFooter(
        pageId, this.pages[pageId].hasFooter,
        Object.keys(this.footerTexts),
        Object.keys(this.footerLines),
        Object.keys(this.footerImages),
        Object.keys(this.footerRectangles),
        Object.keys(this.footerSummaries),
        Object.keys(this.footerPictures),
        Object.keys(this.footerDataPlates)
      );
    }
  }

  public beginDrawMode() {
    for (const elementId of Object.keys(this.elementFinder)) {
      if (this.elementFinder[elementId].getElementType() !== ElementType.Page) {
        this.elementFinder[elementId].isSelected = false;
      }
      this.elementFinder[elementId].isSelectable = false;
    }
    for (const pageId of Object.keys(this.pages)) {
      this.pages[pageId].isDrawMode = true;
    }
  }

  public endDrawMode() {
    for (const elementId of Object.keys(this.elementFinder)) {
      this.elementFinder[elementId].isSelectable = true;
    }
    for (const pageId of Object.keys(this.pages)) {
      this.pages[pageId].isDrawMode = false;
    }
  }

  public drawElement(
    location: Location,
    type: ElementType,
    metadata: ReportPreviewMetadata,
    drawPageId?: string
  ) {

    const instanceMap = this.getInstanceMap(location, type);
    let createdIds: string[] = [];

    // インスタンスの作成
    switch (type) {
      case ElementType.Text:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateText, ReportText>(
            instanceMap, this.textCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateText, ReportText>(
            instanceMap, this.textCreator.bind(this), location
          );
        }
        break;
      case ElementType.Line:
        if (metadata.heightPx >= 0 || metadata.widthPx >= 0) {
          if (drawPageId) {
            createdIds = this.elementFactoryOnPage<TemplateLine, ReportLine>(
              instanceMap, this.lineCreator.bind(this), location,
              this.pages[drawPageId].getIndex(), drawPageId
            );
          } else {
            createdIds = this.elementFactory<TemplateLine, ReportLine>(
              instanceMap, this.lineCreator.bind(this), location
            );
          }
        }
        break;
      case ElementType.Image:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateImage, ReportImage>(
            instanceMap, this.imageCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateImage, ReportImage>(
            instanceMap, this.textCreator.bind(this), location
          );
        }
        break;
      case ElementType.Rectangle:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateRectangle, ReportRectangle>(
            instanceMap, this.rectangleCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateRectangle, ReportRectangle>(
            instanceMap, this.textCreator.bind(this), location
          );
        }
        break;
      case ElementType.Summary:
      case ElementType.Picture:
      case ElementType.DataPlate:
        break;
    }

    // 生成したインスタンスに更新メタデータを設定してプレビュー更新
    createdIds.forEach((elementId: string) => {

      const pageIds = Object.keys(this.pages);
      const element = this.elementFinder[elementId];

      element.setDrawData(metadata);
      element.isSelected = true;

      switch (location) {
        case Location.Header:
          for (const pageId of pageIds) {
            this.previewController.redrawHeader(
              pageId, this.pages[pageId].hasHeader,
              Object.keys(this.headerTexts),
              Object.keys(this.headerLines),
              Object.keys(this.headerImages),
              Object.keys(this.headerRectangles),
              Object.keys(this.headerSummaries),
              Object.keys(this.headerPictures),
              Object.keys(this.headerDataPlates)
            );
          }
          break;
        case Location.Footer:
          for (const pageId of pageIds) {
            this.previewController.redrawFooter(
              pageId, this.pages[pageId].hasFooter,
              Object.keys(this.footerTexts),
              Object.keys(this.footerLines),
              Object.keys(this.footerImages),
              Object.keys(this.footerRectangles),
              Object.keys(this.footerSummaries),
              Object.keys(this.footerPictures),
              Object.keys(this.footerDataPlates)
            );
          }
          break;
        case Location.Body:
          this.previewController.draw(drawPageId, location, type, elementId);
          break;
      }
    });
  }

  public dropElement(elementId: string, type: ElementType, dropMetadata: ReportPreviewMetadata, fromTo: FromTo) {

    const fromInstanceMap = this.getInstanceMap(fromTo.from.location, type);
    const toInstanceMap = this.getInstanceMap(fromTo.to.location, type);
    const dropElement = fromInstanceMap[elementId];
    const dropPageId = fromTo.to.pageId;
    const dropPageIndex = this.elementFinder[dropPageId].getIndex();
    const pageIds = Object.keys(this.pages);

    if (
      fromTo.from.location === fromTo.to.location &&
      fromTo.from.pageId === fromTo.to.pageId
    ) {
      // fromとtoが同じ＝ドラッグアンドドロップでの移動先が同じ場合は
      // instanceMapの更新とpreviewControllerの更新は行わない
    } else {
      // fromとtoが異なる＝ドラッグアンドドロップでの移動先が別の場合は
      // ドラッグアンドドロップによる移動に併せてinstancemapの中身の更新と
      // previewControllerが管理する要素の順番を担保している配列を更新する
      delete fromInstanceMap[elementId];
      toInstanceMap[elementId] = dropElement;
      this.previewController.move(elementId, type, fromTo);

      // 移動する要素のロケーション情報を更新する
      // HeaderかFooterに移動した場合は、Locationのみ指定して更新する（＝pageIndex, pageIdはundefinedで上書きされる＝消える）
      // Bodyに移動した場合は、Location＋pageIndex＋pageIdを指定して更新する
      if (fromTo.to.location === Location.Header || fromTo.to.location === Location.Footer) {
        dropElement.setLocation(fromTo.to.location);
      } else {
        dropElement.setLocation(fromTo.to.location, dropPageId, dropPageIndex);
      }

      // 要素自体が持つindex値を更新する
      // 1ページ上に配置する要素の数や並び順を担保しているのはpreviewControllerなので、
      // previewControllerが保持しているID配列を基に採番し直す
      const fromIds = this.previewController.getIds(fromTo.from.pageId, fromTo.from.location, type);
      for (let fromIdIndex = 0, fromIdMax = fromIds.length; fromIdIndex < fromIdMax; fromIdIndex++) {
        fromInstanceMap[fromIds[fromIdIndex]].setIndex(fromIdIndex);
      }
      const toIds = this.previewController.getIds(fromTo.to.pageId, fromTo.to.location, type);
      for (let toIdIndex = 0, toIdMax = toIds.length; toIdIndex < toIdMax; toIdIndex++) {
        toInstanceMap[toIds[toIdIndex]].setIndex(toIdIndex);
      }
    }

    // 移動する要素のtopとleftを更新する
    dropElement.setDropData(dropMetadata);

    // 移動元がHeader(Header項目が減った)、または移動先がHeader（Header項目が増えた）場合、全ページのHeader項目を再描画する
    if (fromTo.from.location === Location.Header || fromTo.to.location === Location.Header) {
      for (const pageId of pageIds) {
        this.previewController.redrawHeader(
          pageId, this.pages[pageId].hasHeader,
          Object.keys(this.headerTexts),
          Object.keys(this.headerLines),
          Object.keys(this.headerImages),
          Object.keys(this.headerRectangles),
          Object.keys(this.headerSummaries),
          Object.keys(this.headerPictures),
          Object.keys(this.headerDataPlates)
        );
      }
    }

    // 移動元がFooter(Footer項目が減った)、または移動先がFooter（Footer項目が増えた）場合、全ページのFooter項目を再描画する
    if (fromTo.from.location === Location.Footer || fromTo.to.location === Location.Footer) {
      for (const pageId of pageIds) {
        this.previewController.redrawFooter(
          pageId, this.pages[pageId].hasFooter,
          Object.keys(this.footerTexts),
          Object.keys(this.footerLines),
          Object.keys(this.footerImages),
          Object.keys(this.footerRectangles),
          Object.keys(this.footerSummaries),
          Object.keys(this.footerPictures),
          Object.keys(this.footerDataPlates)
        );
      }
    }
  }

  public resizeElement(elementId: string, resizeOffset: ReportPreviewMetadata, markerPointH: string, markerPointV: string) {
    const resizeElement = this.elementFinder[elementId];

    const resizeMetadata = {
      topPx: resizeElement.getTopPx(),
      leftPx: resizeElement.getLeftPx(),
      widthPx: resizeElement.getWidthPx(),
      heightPx: resizeElement.getHeightPx()
    };

    // console.log('resize metadata(before): %s', JSON.stringify(resizeMetadata, undefined, 4));
    switch (markerPointH) {
      case 'left':
        switch (markerPointV) {
          case 'top':
            resizeMetadata.leftPx = resizeMetadata.leftPx + resizeOffset.leftPx;
            resizeMetadata.widthPx = resizeMetadata.widthPx + (resizeOffset.widthPx * -1);
            resizeMetadata.topPx = resizeMetadata.topPx + resizeOffset.topPx;
            resizeMetadata.heightPx = resizeMetadata.heightPx + (resizeOffset.heightPx * -1);
            break;
          case 'middle':
            resizeMetadata.leftPx = resizeMetadata.leftPx + resizeOffset.leftPx;
            resizeMetadata.widthPx = resizeMetadata.widthPx + (resizeOffset.widthPx * -1);
            break;
          case 'bottom':
            resizeMetadata.leftPx = resizeMetadata.leftPx + resizeOffset.leftPx;
            resizeMetadata.widthPx = resizeMetadata.widthPx + (resizeOffset.widthPx * -1);
            resizeMetadata.heightPx = resizeMetadata.heightPx + resizeOffset.heightPx;
            break;
        }
        break;

      case 'center':
        switch (markerPointV) {
          case 'top':
            resizeMetadata.topPx = resizeMetadata.topPx + resizeOffset.topPx;
            resizeMetadata.heightPx = resizeMetadata.heightPx + (resizeOffset.heightPx * -1);
            break;
          case 'middle':
            // 要素の上下左右中央にマーカーは配置していないため処理なし
            break;
          case 'bottom':
            resizeMetadata.heightPx = resizeMetadata.heightPx + resizeOffset.heightPx;
            break;
        }
        break;

      case 'right':
        switch (markerPointV) {
          case 'top':
            resizeMetadata.topPx = resizeMetadata.topPx + resizeOffset.topPx;
            resizeMetadata.widthPx = resizeMetadata.widthPx + resizeOffset.widthPx;
            resizeMetadata.heightPx = resizeMetadata.heightPx + (resizeOffset.heightPx * -1);
            break;
          case 'middle':
            resizeMetadata.widthPx = resizeMetadata.widthPx + resizeOffset.widthPx;
            break;
          case 'bottom':
            resizeMetadata.widthPx = resizeMetadata.widthPx + resizeOffset.widthPx;
            resizeMetadata.heightPx = resizeMetadata.heightPx + resizeOffset.heightPx;
            break;
        }
        break;
    }

    // console.log('resize metadata(after): %s', JSON.stringify(resizeMetadata, undefined, 4));

    resizeElement.setResizeData(resizeMetadata);
  }

  public updateCommonProperties(metadata: UpdateMetadata) {
    const properties = this.getCommonProperties();
    for (const props of properties) {
      if (props.getId() === metadata.id) {
        props.updateProperties(metadata);
        break;
      }
    }
  }

  public updateElementProperties(metadata: UpdateMetadata) {
    const element = this.elementFinder[metadata.id];

    element.updateProperties(metadata);

    if (element.getElementType() === ElementType.Page && metadata.name === 'hasHeader') {
      this.previewController.redrawHeader(
        metadata.id, metadata.value as boolean,
        Object.keys(this.headerTexts),
        Object.keys(this.headerLines),
        Object.keys(this.headerImages),
        Object.keys(this.headerRectangles),
        Object.keys(this.headerSummaries),
        Object.keys(this.headerPictures),
        Object.keys(this.headerDataPlates)
      );
    }
    if (element.getElementType() === ElementType.Page && metadata.name === 'hasFooter') {
      this.previewController.redrawFooter(
        metadata.id, metadata.value as boolean,
        Object.keys(this.footerTexts),
        Object.keys(this.footerLines),
        Object.keys(this.footerImages),
        Object.keys(this.footerRectangles),
        Object.keys(this.footerSummaries),
        Object.keys(this.footerPictures),
        Object.keys(this.footerDataPlates)
      );
    }
  }

  public selectElement(targetId: string, isCtrl: boolean) {
    const selectedElement = this.elementFinder[targetId];
    if (isCtrl) {
      selectedElement.isSelected = !selectedElement.isSelected;
    } else {
      this.deselectAllCore();
      selectedElement.isSelected = true;
    }
  }
  public changePage(targetId: string) {
    const selectedElement = this.elementFinder[targetId];
    this.deselectAllPage();
    selectedElement.isSelected = true;
  }

  public deselectAll(isCtrl: boolean) {
    if (!isCtrl) {
      this.deselectAllCore();
    }
  }

  public deleteSelected(): void {
    for (const elementId of Object.keys(this.elementFinder)) {
      if (this.elementFinder[elementId].isSelected && this.elementFinder[elementId].getElementType() !== ElementType.Page
        && this.elementFinder[elementId].getElementType() !== ElementType.Summary
        && this.elementFinder[elementId].getElementType() !== ElementType.Picture
        && this.elementFinder[elementId].getElementType() !== ElementType.DataPlate) {
        const location = this.elementFinder[elementId].getLocation();
        if (this.elementFinder[elementId].getPageId()) {
          this.previewController.remove(this.elementFinder[elementId].getPageId(),
            location, this.elementFinder[elementId].getId());
          this.removeBodyElement(elementId);
        } else {
          const pageIds = Object.keys(this.pages);
          // Header項目が減った場合、全ページのHeader項目を再描画する
          if (location === Location.Header) {
            this.removeHeaderElement(elementId);
            for (const pageId of pageIds) {
              this.previewController.redrawHeader(
                pageId, this.pages[pageId].hasHeader,
                Object.keys(this.headerTexts),
                Object.keys(this.headerLines),
                Object.keys(this.headerImages),
                Object.keys(this.headerRectangles),
                Object.keys(this.headerSummaries),
                Object.keys(this.headerPictures),
                Object.keys(this.headerDataPlates)
              );
            }
          }

          // Footer項目が減った場合、全ページのFooter項目を再描画する
          if (location === Location.Footer) {
            this.removeFooterElement(elementId);
            for (const pageId of pageIds) {
              this.previewController.redrawFooter(
                pageId, this.pages[pageId].hasFooter,
                Object.keys(this.footerTexts),
                Object.keys(this.footerLines),
                Object.keys(this.footerImages),
                Object.keys(this.footerRectangles),
                Object.keys(this.footerSummaries),
                Object.keys(this.footerPictures),
                Object.keys(this.footerDataPlates)
              );
            }
          }
        }
      }
    }
  }
  public removeHeaderElement(elementId: string): void {
    this.remove(this.headerTexts, elementId);
    this.remove(this.headerLines, elementId);
    this.remove(this.headerImages, elementId);
    this.remove(this.headerRectangles, elementId);
  }
  public removeBodyElement(elementId: string): void {
    this.remove(this.bodyTexts, elementId);
    this.remove(this.bodyLines, elementId);
    this.remove(this.bodyImages, elementId);
    this.remove(this.bodyRectangles, elementId);
  }
  public removeFooterElement(elementId: string): void {
    this.remove(this.footerTexts, elementId);
    this.remove(this.footerLines, elementId);
    this.remove(this.footerImages, elementId);
    this.remove(this.footerRectangles, elementId);
  }

  public remove(target, elementId): void {
    if (elementId in target) {
      delete target[elementId];
    }
  }

  public getCommonProperties(): ReportProperties[] {
    return [
      // this.units,
      this.fonts,
      this.paperSize,
      this.pageMargin,
      this.pageHeader,
      this.pageFooter
    ];
  }

  public getPaperHeightPx(): number {
    return this.paperSize.getHeightPx();
  }

  public getSelectedElementProperties(): ReportProperties[] {
    const rtnArray = new Array<ReportProperties>();
    for (const elementId of Object.keys(this.elementFinder)) {
      if (this.elementFinder[elementId].isSelected) {
        rtnArray.push(this.elementFinder[elementId]);
      }
    }
    return rtnArray;
  }

  private deselectAllCore() {
    for (const elementId of Object.keys(this.elementFinder)) {
      if (this.elementFinder[elementId].getElementType() !== ElementType.Page) {
        this.elementFinder[elementId].isSelected = false;
      }
    }
  }
  private deselectAllPage() {
    for (const elementId of Object.keys(this.elementFinder)) {
      if (this.elementFinder[elementId].getElementType() === ElementType.Page) {
        this.elementFinder[elementId].isSelected = false;
      }
    }
  }

  private elementFactory<T, R extends ReportElement>(
    dest: { [elementId: string]: R },
    creator: (index: number, templateDef?: T) => R,
    location: Location,
    src?: Array<T>
  ): Array<string> {

    const createdIds = new Array<string>();

    // インスタンスの生成＋指定された格納先へ格納
    if (!src) {

      // destのプロパティ配列の要素数＝次に設定するプロパティのindexと同等なので
      // -1などせずそのまま与える
      const myIndex = Object.keys(dest).length;
      const instance = creator(myIndex);
      const elementId = instance.getId();

      createdIds.push(elementId);
      instance.setLocation(location);

      dest[elementId] = instance;
      this.elementFinder[elementId] = instance;

    } else {

      for (let i = 0, max = src.length; i < max; i++) {

        const instance = creator(i, src[i]);
        const elementId = instance.getId();

        createdIds.push(elementId);
        instance.setLocation(location);

        dest[elementId] = instance;
        this.elementFinder[elementId] = instance;
      }
    }

    return createdIds;
  }

  private elementFactoryOnPage<T, R extends ReportElement>(
    dest: { [elementId: string]: R },
    creator: (index: number, templateDef?: T) => R,
    location: Location,
    pageIndex: number,
    pageId: string,
    src?: Array<T>,
  ): Array<string> {

    const createdIds = new Array<string>();

    if (!src) {
      const myIndex = Object.keys(this.instanceMapFilter(dest, pageId)).length;
      const instance = creator(myIndex);
      const elementId = instance.getId();

      createdIds.push(elementId);
      instance.setLocation(location, pageId, pageIndex);

      dest[elementId] = instance;
      this.elementFinder[elementId] = instance;

    } else {
      // インスタンスの生成＋指定された格納先へ格納
      for (let i = 0, max = src.length; i < max; i++) {

        const instance = creator(i, src[i]);
        const elementId = instance.getId();

        createdIds.push(elementId);
        instance.setLocation(location, pageId, pageIndex);

        dest[elementId] = instance;
        this.elementFinder[elementId] = instance;
      }
    }

    return createdIds;
  }

  private pageCreator(index: number, templatePage?: TemplatePageSection): ReportPage {
    return new ReportPage(
      index, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templatePage
    );
  }
  private textCreator(index: number, templateText?: TemplateText): ReportText {
    return new ReportText(index, this.units, this.fonts, templateText);
  }
  private lineCreator(index: number, templateLine?: TemplateLine): ReportLine {
    return new ReportLine(index, this.units, templateLine);
  }
  private imageCreator(index: number, templateImage?: TemplateImage): ReportImage {
    return new ReportImage(index, this.units, this.fonts, templateImage);
  }
  private rectangleCreator(index: number, templateRectangle?: TemplateRectangle): ReportRectangle {
    return new ReportRectangle(index, this.units, templateRectangle);
  }
  private summaryCreator(index: number, templateSummary?: TemplateSummary): ReportSummary {
    return new ReportSummary(ElementType.Summary, index, this.units, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templateSummary);
  }
  private pictureCreator(index: number, templatePicture?: TemplateSummary): ReportSummary {
    return new ReportSummary(ElementType.Picture, index, this.units, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templatePicture);
  }
  private dataPlateCreator(index: number, templateDataPlate?: TemplateDataPlate): ReportDataPlate {
    return new ReportDataPlate(ElementType.DataPlate, index, this.units, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templateDataPlate);
  }

  private instanceMapFilter<R extends ReportElement>(src: { [elementId: string]: R }, pageId: string): { [elementId: string]: R } {
    const rtn: { [elementId: string]: R } = {};
    for (const elementId of Object.keys(src)) {
      if (src[elementId].getPageId() === pageId) {
        rtn[elementId] = src[elementId];
      }
    }
    return rtn;
  }

  // private getInstanceMap(location: Location, type: ElementType): { [elementId: string]: ReportText };
  // private getInstanceMap(location: Location, type: ElementType): { [elementId: string]: ReportLine };
  // private getInstanceMap(location: Location, type: ElementType): { [elementId: string]: ReportImage };
  // private getInstanceMap(location: Location, type: ElementType): { [elementId: string]: ReportRectangle };
  private getInstanceMap(location: Location, type: ElementType): any {
    switch (location) {
      case Location.Header:
        switch (type) {
          case ElementType.Text:
            return this.headerTexts;
          case ElementType.Line:
            return this.headerLines;
          case ElementType.Image:
            return this.headerImages;
          case ElementType.Rectangle:
            return this.headerRectangles;
          case ElementType.Summary:
            return this.headerSummaries;
          case ElementType.Picture:
            return this.headerPictures;
          case ElementType.DataPlate:
            return this.headerDataPlates;
          default:
            return null;
        }
      case Location.Footer:
        switch (type) {
          case ElementType.Text:
            return this.footerTexts;
          case ElementType.Line:
            return this.footerLines;
          case ElementType.Image:
            return this.footerImages;
          case ElementType.Rectangle:
            return this.footerRectangles;
          case ElementType.Summary:
            return this.footerSummaries;
          case ElementType.Picture:
            return this.footerPictures;
          case ElementType.DataPlate:
            return this.footerDataPlates;
          default:
            return null;
        }
      case Location.Body:
        switch (type) {
          case ElementType.Text:
            return this.bodyTexts;
          case ElementType.Line:
            return this.bodyLines;
          case ElementType.Image:
            return this.bodyImages;
          case ElementType.Rectangle:
            return this.bodyRectangles;
          case ElementType.Summary:
            return this.bodySummaries;
          case ElementType.Picture:
            return this.bodyPictures;
          case ElementType.DataPlate:
            return this.bodyDataPlates;
          default:
            return null;
        }
    }
  }

  public alignElementsWithSection(pageId: string): void {
    this.getIds(pageId, Location.Body, ElementType.Section).forEach((sectionId) => {
      const section = this.elementFinder[sectionId] as ReportSection;
      const position = {
        xStart: section.getTemplateJSON().x,
        xEnd: section.getTemplateJSON().x + section.getTemplateJSON().width,
        yStart: section.getTemplateJSON().y,
        yEnd: section.getTemplateJSON().y + section.getTemplateJSON().height
      };

      this.filterElementsInSection(pageId, ElementType.Text, position, section);
      this.filterElementsInSection(pageId, ElementType.Line, position, section);
      this.filterElementsInSection(pageId, ElementType.Image, position, section);
      this.filterElementsInSection(pageId, ElementType.Rectangle, position, section);
      this.filterElementsInSection(pageId, ElementType.Summary, position, section);
      this.filterElementsInSection(pageId, ElementType.Picture, position, section);
      this.filterElementsInSection(pageId, ElementType.DataPlate, position, section);
      this.filterElementsInSection(pageId, ElementType.MeasurementPoint, position, section);
      this.filterElementsInSection(pageId, ElementType.VisualEvents, position, section);
      this.filterElementsInSection(pageId, ElementType.Signature, position, section);
      this.filterElementsInSection(pageId, ElementType.Table, position, section);
      this.filterElementsInSection(pageId, ElementType.InteractiveIso, position, section);
      this.filterElementsInSection(pageId, ElementType.DropDownList, position, section);
    });
  }

  private filterElementsInSection<R extends ReportElement>(pageId: string, elType: ElementType, position: any, section: ReportSection)
    : { [elementId: string]: R } {
    const rtn: { [elementId: string]: R } = {};
    this.getIds(pageId, Location.Body, elType).forEach((itemId) => {
      const item = this.elementFinder[itemId] as any;
      if (item.getTemplateJSON().x >= position.xStart && item.getTemplateJSON().x <= position.xEnd &&
        item.getTemplateJSON().y >= position.yStart && item.getTemplateJSON().y <= position.yEnd) {
        const location: ElementLocationInSection = {
          x: item.units.concatenate('sizeUnit', (item.getTemplateJSON().x - position.xStart) as number),
          y: item.units.concatenate('sizeUnit', (item.getTemplateJSON().y - position.yStart) as number)
        };
        item.setSectionInfo(section.getTemplateJSON().id, section.stageId, location);
      } else if (item.getTemplateJSON().sectionId === section.getId()) {
        item.setSectionInfo('', '', undefined);
      }
    });
    return rtn;
  }
}

export class ReportSignature extends ReportElement {
  public top: string;
  public left: string;
  public zIndex: number;
  public width: string;
  public height: string;
  public borderStyle: string;
  public borderColor: string;
  public borderWidth: string;
  public reservedFor: string;

  private units: ReportUnits;

  private baseDef: TemplateSignature;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaBorderStyle = 'borderStyle';
  private readonly metaBorderColor = 'borderColor';
  private readonly metaBorderWidth = 'borderWidth';
  private readonly metaReservedFor = 'reservedFor';

  constructor(index: number, units: ReportUnits, fonts: ReportFonts, templateSignature?: TemplateSignature) {

    super(ElementType.Signature, index, !(templateSignature == null), templateSignature ? templateSignature.id : null);

    this.units = units;

    if (templateSignature) {
      this.baseDef = templateSignature;
      this.baseDef.id = this.baseDef.id ? this.baseDef.id : this.generateUUID();
    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        zIndex: 0,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#a0a0a0',
        reservedFor: ''
      };
    }
    this.zIndex = this.baseDef.zIndex;

    this.top = this.units.concatenate('sizeUnit', this.baseDef.y);
    this.left = this.units.concatenate('sizeUnit', this.baseDef.x);

    this.width = this.getWidth();
    this.height = this.getHeight();
    this.borderWidth = this.units.concatenate('sizeUnit', this.baseDef.borderWidth);
    this.borderStyle = this.baseDef.borderStyle;
    this.borderColor = this.baseDef.borderColor;
    this.reservedFor = this.baseDef.reservedFor;

    this.setChainUpdate();
    this.setPreviewMetadata();
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
    const width = this.units.fromPx('sizeUnit', metadata.widthPx) < 80 ? 80 : this.units.fromPx('sizeUnit', metadata.widthPx);
    const height = this.units.fromPx('sizeUnit', metadata.heightPx) < 55 ? 55 : this.units.fromPx('sizeUnit', metadata.heightPx);
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: width,
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: height,
      oldValue: this.baseDef[this.metaHeight]
    });
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }
  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.getWidthNum());
  }
  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.getHeightNum());
  }

  public getTemplateJSON(): TemplateSignature {
    return this.baseDef;
  }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: 'Position'
      },
      {
        name: this.metaTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: 'Position'
      },
      {
        name: this.metaBorderWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: 'Line'
      },
      {
        name: this.metaBorderColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        group: 'Line'
      },
      {
        name: this.metaBorderStyle, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'none', value: 'none' },
          { label: 'hidden', value: 'hidden' },
          { label: 'solid', value: 'solid' },
          { label: 'double', value: 'double' },
          { label: 'groove', value: 'groove' },
          { label: 'ridge', value: 'ridge' },
          { label: 'inset', value: 'inset' },
          { label: 'outset', value: 'outset' },
          { label: 'dashed', value: 'dashed' },
          { label: 'dotted', value: 'dotted' },
        ],
        isRequired: false,
        isEdited: false,
        group: 'Line'
      },
      {
        name: this.metaReservedFor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'Inspector', value: 'Inspector' },
          { label: 'Reviewer', value: 'Reviewer' }
        ],
        isRequired: false,
        isEdited: false,
      }
    ];
  }

  public setSectionInfo(id: string, stageName: string, location: ElementLocationInSection): void {
    this.baseDef.sectionId = id;
    this.baseDef.sectionStageTag = stageName;
    this.baseDef.elementLocationInSection = location;
  }

  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.getWidth();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.getHeight();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.name] = metadata.value as number;
        this.zIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.borderWidth = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderStyle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaReservedFor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.reservedFor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.convertUnit(this.baseDef[this.metaWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaWidth]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaHeight,
          value: this.convertUnit(this.baseDef[this.metaHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaHeight]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaBorderWidth,
          value: this.convertUnit(this.baseDef[this.metaBorderWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaBorderWidth]
        });
      })
    );
  }

  private getWidthNum(): number {
    return this.baseDef.width;
  }
  private getHeightNum(): number {
    return this.baseDef.height;
  }
  private getWidth(): string {
    return this.units.concatenate('sizeUnit', this.getWidthNum());
  }
  private getHeight(): string {
    return this.units.concatenate('sizeUnit', this.getHeightNum());
  }
}
